/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { ClearableSearchInput } from "components/SearchField/SearchField";
import CatalogueItemDialog from "components/CatalogueItemDialog";
import CatalogueItemDropdown from "components/CatalogueItemDropdown";
import { PageSkeleton } from "components/Skeleton";
import AddItemDialog from "layouts/Items/AddItemDialog";
import ThemedSelect from "components/FormControls/ThemedSelect";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import validator from "validator";
import { useCatalogueAccountOptions } from "utils/catalogueAccounts";
import {
  LuArrowLeft,
  LuBell,
  LuCheck,
  LuChevronDown,
  LuCircleAlert,
  LuCircleHelp,
  LuCoins,
  LuCopy,
  LuDollarSign,
  LuDownload,
  LuEye,
  LuFileText,
  LuGripVertical,
  LuInfo,
  LuLock,
  LuMenu,
  LuUpload,
  LuPanelRightClose,
  LuPercent,
  LuPencil,
  LuPlus,
  LuSave,
  LuSearch,
  LuSend,
  LuTrash2,
  LuX,
  LuChevronsUpDown,
} from "react-icons/lu";

import { useMaterialUIController } from "context";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { appConstants } from "utils/appConstants";
import {
  convertToTitleCase,
  getScreenRight,
  paymentTermsOptions,
  showResMsg,
  validateField,
} from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import {
  downloadQuotation,
  prepareQuotationEmail,
  prepareQuotationEmailDocument,
  getContactsForQuotation,
  getItemsForQuotation,
  getQuotationDetailsByID,
  SaveQuotation,
  UpdateQuotation,
} from "utils/quotationAPI";
import {
  addCatalogueItemData,
  fetchActivityLog,
  getCurrencyCode,
  saveNotes,
} from "utils/api";
import { formatActivityTimestamp } from "utils/activityTimestamp";
import { addContactData } from "utils/contactAPI";
import {
  normalizeQuotationCustomerField,
  sanitizeAustralianMobileInput,
  parseAdditionalQuotationEmails,
  validateAdditionalQuotationEmails,
  validateQuotationCustomerField,
  validateQuotationCustomerForm,
} from "utils/quotationCustomerValidation";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { sanitizeQuantityInputValue } from "utils/numberInputConstraints";
import {
  createPendingAttachment,
  uploadPendingAttachments,
} from "utils/documentAttachments";
import { captureWebsiteDocument } from "utils/documentPdf";
import {
  DashboardNavigation,
  ProfileMenu,
} from "layouts/businessUserDashboard";
import AppHeader from "components/AppHeader";
import {
  feedbackBus,
  InlineFormMessage,
} from "components/InlineFeedback/InlineFeedback";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import QuotationRecipientField from "./QuotationRecipientField";
import DocumentSentSuccessDialog from "./DocumentSentSuccessDialog";
import BillingModal from "components/displayModel/billingModel";
import ShowSwalWithButtons from "components/SwalAlert/swalPopupWithButtons";
import PickDate from "components/DatePicker";
import useDocumentNumberValidation from "hooks/useDocumentNumberValidation";
import { clearQuotationNumberPreview, getQuotationNumberPreview } from "utils/quotationNumberPreview";

import "./quotation.css";
import useUnsavedChanges from "hooks/useUnsavedChanges";
import useUnsavedModalChanges from "hooks/useUnsavedModalChanges";

const EMPTY_ITEM_ROW = {
  itemID: "",
  itemName: "",
  itemDescription: "",
  itemPrice: "",
  itemQuantity: 1,
  categoryCodeName: "",
  gst: 10,
  discountType: "None", // None | Percentage | Fixed
  discountValue: "",
  discountAmount: 0,
  totalItemPrice: 0,
};

const MAX_VISIBLE_ITEM_ROWS = 4;

function generateQuotationTerms(validity = "14 Days") {
  const term = String(validity).toLowerCase();
  const days = parseInt(term, 10) || 14;
  const validityText = term.includes("same day")
    ? "This quote is valid until the end of the day it is sent."
    : term.includes("end of month")
    ? `This quote is valid for ${days} days after the end of the month it is sent.`
    : `This quote is valid for ${days} days from sending.`;
  return `Listed supply and installation only. Additional work requires approval. ${validityText}`;
}

function getQuotationValidityText(validity = "14 Days") {
  const term = String(validity).toLowerCase();
  const days = parseInt(term, 10) || 14;

  if (term.includes("same day")) {
    return "Valid until the end of the day it is sent.";
  }

  if (term.includes("end of month")) {
    return `Valid for ${days} days after the end of the month it is sent.`;
  }

  return `Valid for ${days} days from sending.`;
}

function getRecipientEmailError(value, required = false) {
  const email = String(value || "").trim();
  if (!email) return required ? "Recipient email cannot be empty." : "";
  return validator.isEmail(email)
    ? ""
    : "Please enter a valid email address, e.g. accounts@example.com.";
}

function formatAustralianMobile(value) {
  const compact = value.trim().replace(/[\s()-]/g, "");
  if (!/^\+?\d*$/.test(compact)) return value;
  const national = compact.startsWith("+61")
    ? compact.slice(3)
    : compact.startsWith("61")
    ? compact.slice(2)
    : compact.startsWith("04")
    ? compact.slice(1)
    : compact.startsWith("4")
    ? compact
    : null;
  if (national === null) return compact;
  return national ? `+61 ${national.match(/.{1,3}/g).join(" ")}` : "+61";
}

function CatalogueDropdown({
  value,
  options,
  onChange,
  ariaLabel,
  portalMenu = false,
  disabled = false,
}) {
  return (
    <ThemedSelect
      aria-label={ariaLabel}
      value={value}
      disabled={disabled}
      menuPortalContainerSelector={
        portalMenu ? ".quotation-editor-shell" : undefined
      }
      disableMenuFlip={portalMenu}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </ThemedSelect>
  );
}

CatalogueDropdown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  portalMenu: PropTypes.bool,
  disabled: PropTypes.bool,
};

function getStoredLoginDetails() {
  try {
    return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
  } catch {
    return {};
  }
}

function getOrganisationName(details = {}) {
  const customName = `${details.firstName || ""} ${
    details.lastName || ""
  }`.trim();
  return (
    details.businessName ||
    details.companyName ||
    details.organisationName ||
    details.organizationName ||
    details.fullName ||
    details.name ||
    customName ||
    "Invoice & Recover"
  );
}

function AddQuotation() {
  const [controller, controllerDispatch] = useMaterialUIController();
  const { darkMode } = controller;

  const navigate = useNavigate();
  const location = useLocation();
  const revisionPendingRef = useRef(
    location.state?.revisionRequested === true
  );
  const dispatch = useDispatch();
  const { id: QuotationID } = useParams();
  const type = new URLSearchParams(window.location.search).get("type");

  // Authentication & Org info
  const loginDetails = useMemo(() => getStoredLoginDetails(), []);
  const catalogueAccountOptions = useCatalogueAccountOptions(
    loginDetails?.businessUserID
  );
  const userName =
    `${loginDetails.firstName || ""} ${loginDetails.lastName || ""}`.trim() ||
    "User";
  const role = loginDetails.roleName || "Owner";
  const organisation = getOrganisationName(loginDetails);

  // App UI states
  const [navOpen, setNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [currencyCode, setCurrencyCode] = useState("AUD");
  const [screenRight, setScreenRight] = useState("Full Access");
  const [isDirty, setIsDirty] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isEditorLoading, setIsEditorLoading] = useState(Boolean(QuotationID));

  // Form errors
  const [isQuoteNumberLoading, setIsQuoteNumberLoading] = useState(
    !QuotationID
  );
  const quoteNumberEditedRef = useRef(false);
  const numberValidation = useDocumentNumberValidation("quotation", "quotation-number-input");
  const [contactNameErr, setContactNameErr] = useState(false);
  const [recipientEmailError, setRecipientEmailError] = useState("");
  const [draftValidationAttempted, setDraftValidationAttempted] =
    useState(false);
  const [draftValidationField, setDraftValidationField] = useState(null);
  const [fileError, setFileError] = useState("");

  // Options & Catalogue
  const [contactDetails, setContactDetails] = useState([]);
  const [masterItemDetails, setMasterItemDetails] = useState([]);

  // Combobox Active Dropdowns
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const customerDropdownRef = useRef(null);

  const [activeItemDropdownIdx, setActiveItemDropdownIdx] = useState(null);
  const [itemSearchQueries, setItemSearchQueries] = useState({});
  const itemDropdownRefs = useRef({});
  const itemDragRef = useRef(null);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  // Quick Create Modals
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCustomerSaving, setIsCustomerSaving] = useState(false);
  const [canManageCustomers, setCanManageCustomers] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [isCustomerDeleting, setIsCustomerDeleting] = useState(false);
  const [customerDeleteError, setCustomerDeleteError] = useState("");
  const [newCustomerForm, setNewCustomerForm] = useState({
    companyName: "",
    contactPerson: "",
    mobile: "+61",
    email: "",
    abn: "",
    paymentTerms: "14 Days",
    autoReminders: true,
  });
  const [customerModalError, setCustomerModalError] = useState("");
  const [customerFieldErrors, setCustomerFieldErrors] = useState({});
  const customerModalChanges = useUnsavedModalChanges({
    open: isCustomerModalOpen,
    values: newCustomerForm,
    busy: isCustomerSaving,
    onClose: () => setIsCustomerModalOpen(false),
  });

  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingCatalogueItem, setEditingCatalogueItem] = useState(null);
  const [itemModalTargetIdx, setItemModalTargetIdx] = useState(null);
  const [isItemSaving, setIsItemSaving] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    itemName: "",
    itemCode: "",
    itemDescription: "",
    itemPrice: "",
    categoryCodeName: "200 - Sales",
    status: "Active",
    unit: "each",
    defaultQuantity: 1,
    gst: 10,
    discountType: "None",
    discountValue: "",
  });
  const [itemModalError, setItemModalError] = useState("");

  // Send Quote Modal
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sentQuotationId, setSentQuotationId] = useState("");
  const openedSendFromPreviewRef = useRef(false);
  const sendDocumentRef = useRef(null);
  const [sendRecipient, setSendRecipient] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [additionalRecipientsError, setAdditionalRecipientsError] =
    useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [sendCopy, setSendCopy] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendError, setSendError] = useState("");
  const [isSavingQuote, setIsSavingQuote] = useState(false);
  const savedQuotationRef = useRef(null);

  // Document Viewer Modal
  const [viewDocumentInModal, setViewDocumentInModal] = useState(false);
  const [documentFileURL, setDocumentFileURL] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");

  useEffect(() => {
    if (!viewDocumentInModal) return;
    return () => {
      if (documentFileURL.startsWith("blob:"))
        URL.revokeObjectURL(documentFileURL);
    };
  }, [documentFileURL, viewDocumentInModal]);

  // Billing Modal
  const [showBillingModal, setShowBillingModal] = useState(false);

  // History & notes card
  const [historyRows, setHistoryRows] = useState([]);
  const [internalNote, setInternalNote] = useState("");

  // Main Form Values
  const [values, setValues] = useState({
    token: sessionStorage.getItem("TOKEN"),
    businessUserID: loginDetails?.businessUserID,
    userID: loginDetails?._id,
    quotationID: QuotationID,
    quotationNumber: "",
    contactID: "",
    name: "",
    recipientEmail: "",
    purchaseOrderNumber: "",
    quotationDate: new Date(),
    validUpto: "14 Days",
    validUptoDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    quotationStatus: "Draft",
    gstType: "Inclusive",
    totalAmount: "0.00",
    totalDiscountAmount: "0.00",
    totalGSTAmount: "0.00",
    finalAmount: "0.00",
    paymentInstruction: "",
    terms: generateQuotationTerms(),
    hasQuo_DocumentFile: false,
    emailSent: 0,
    createdAt: new Date().toISOString(),
    createdByName: userName,
  });

  // Line Items
  const [itemRowValues, setItemRowValues] = useState([{ ...EMPTY_ITEM_ROW }]);
  const [itemsViewportHeight, setItemsViewportHeight] = useState(null);
  const itemsTableBodyRef = useRef(null);
  const pendingItemsScrollRef = useRef(null);
  const [quoDocuments, setDocuments] = useState([]);
  const saveAndExitRef = useRef(null);
  const unsavedChanges = useUnsavedChanges(
    isDirty || quoDocuments.some((document) => document.file),
    {
      documentName: "quotation",
      isExistingDraft: Boolean(QuotationID || values.quotationID),
      canSaveDraft: screenRight === "Full Access" && values.quotationStatus === "Draft",
      onSave: async () => {
        let saved = false;
        await handleSaveQuotation(() => { saved = true; }, { redirectAfterSave: false });
        return saved;
      },
    }
  );

  useLayoutEffect(() => {
    const tableBody = itemsTableBodyRef.current;
    if (!tableBody || itemRowValues.length < MAX_VISIBLE_ITEM_ROWS) {
      setItemsViewportHeight(null);
      return undefined;
    }

    const visibleRows = Array.from(tableBody.children).slice(
      0,
      MAX_VISIBLE_ITEM_ROWS
    );
    const updateViewportHeight = () => {
      const height = visibleRows.reduce(
        (total, row) => total + row.getBoundingClientRect().height,
        0
      );
      if (height > 0) setItemsViewportHeight(Math.ceil(height));
    };

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateViewportHeight)
        : null;
    visibleRows.forEach((row) => resizeObserver?.observe(row));

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      resizeObserver?.disconnect();
    };
  }, [itemRowValues.length]);

  useLayoutEffect(() => {
    const tableBody = itemsTableBodyRef.current;
    const scrollTarget = pendingItemsScrollRef.current;
    if (!tableBody || !scrollTarget) return undefined;

    pendingItemsScrollRef.current = null;
    const animationFrame = window.requestAnimationFrame(() => {
      const top = scrollTarget === "bottom" ? tableBody.scrollHeight : 0;
      if (typeof tableBody.scrollTo === "function") {
        tableBody.scrollTo({
          top,
          behavior:
            scrollTarget === "bottom" &&
            !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
              ? "smooth"
              : "auto",
        });
      } else {
        tableBody.scrollTop = top;
      }
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [itemRowValues]);
  const allDraftItemErrors = useMemo(() => {
    return itemRowValues.map((row, index) => {
      const name = String(row.itemName || "").trim();
      const desc = String(row.itemDescription || "").trim();
      const hasContent = Boolean(
        name || desc || row.itemID || String(row.itemPrice ?? "").trim()
      );
      // Preserve the existing behavior of ignoring extra completely empty trailing rows.
      if (!hasContent && itemRowValues.length > 1 && index > 0) return {};
      const errors = {};
      if (!name && !desc) {
        errors.itemName = "Enter an item name or description.";
      }
      if (!String(row.itemPrice ?? "").trim())
        errors.itemPrice = "Enter a unit price.";
      else if (
        !Number.isFinite(Number(row.itemPrice)) ||
        Number(row.itemPrice) < 0
      ) {
        errors.itemPrice = "Enter 0 or more.";
      }
      if (
        !Number.isFinite(Number(row.itemQuantity)) ||
        Number(row.itemQuantity) <= 0
      ) {
        errors.itemQuantity = "Must be > 0.";
      }
      return errors;
    });
  }, [itemRowValues]);
  const draftItemErrors = allDraftItemErrors.map((errors, index) => {
    const field = Object.keys(errors).find(
      (name) =>
        draftValidationField ===
        `quotation-${name === "itemName" ? "item-search" : name}-${index}`
    );
    return field ? { [field]: errors[field] } : {};
  });
  const draftDateError =
    !values.quotationDate ||
    Number.isNaN(new Date(values.quotationDate).getTime())
      ? "Choose a valid quote date."
      : "";
  const visibleRecipientEmailError =
    draftValidationField === "quotation-recipient-email"
      ? recipientEmailError
      : "";

  useEffect(() => {
    if (QuotationID) {
      setIsQuoteNumberLoading(false);
      return undefined;
    }
    let active = true;
    setIsQuoteNumberLoading(true);
    getQuotationNumberPreview(loginDetails?.businessUserID)
      .then((quotationNumber) => {
        if (!active) return;
        setValues((previous) =>
          previous.quotationID || quoteNumberEditedRef.current
            ? previous
            : { ...previous, quotationNumber }
        );
      })
      .catch(() => {
        if (active)
          showResMsg(
            "Could not generate the quote number. Please try again.",
            2000,
            false
          );
      })
      .finally(() => {
        if (active) setIsQuoteNumberLoading(false);
      });
    return () => { active = false; };
  }, [QuotationID, loginDetails?.businessUserID]);

  // Fetch initial right, currency, contacts, and quotation
  useEffect(() => {
    const init = async () => {
      if (QuotationID && savedQuotationRef.current === String(QuotationID))
        return;
      setIsEditorLoading(Boolean(QuotationID));
      savedQuotationRef.current = null;
      getScreenRight("Quotations")
        .then(setScreenRight)
        .catch((err) => {
          console.error("Right fetch error:", err);
        });
      getScreenRight("Contacts")
        .then((right) => setCanManageCustomers(right === "Full Access"))
        .catch(() => setCanManageCustomers(false));

      const contactsRequest = getContactsForQuotation(
        { businessUserID: loginDetails?.businessUserID },
        (data) => {
          if (data?.Result_Output?.length) {
            setContactDetails(data.Result_Output);
          }
        },
        dispatch
      );

      const currencyRequest = getCurrencyCode(
        { businessUserID: loginDetails?.businessUserID },
        (data) => {
          if (data?.Result_Status && data.Result_Output) {
            setCurrencyCode(data.Result_Output);
          }
        },
        dispatch
      );

      const itemsRequest = getItemsForQuotation(
        { businessUserID: loginDetails?.businessUserID },
        (data) => {
          if (data?.Result_Output?.length) {
            setMasterItemDetails(data.Result_Output);
          }
        },
        dispatch
      );

      const supportingRequests = Promise.all([
        contactsRequest,
        currencyRequest,
        itemsRequest,
      ]);

      if (QuotationID) {
        dispatch(loadingAction(true));
        const quotationRequest = getQuotationDetailsByID(
          {
            quotationID: QuotationID,
            businessUserID: loginDetails?.businessUserID,
          },
          (data) => {
            dispatch(loadingAction(false));
            if (data?.Result_Status && data.Result_Output?.[0]) {
              const result = data.Result_Output[0];
              const custName =
                result.name ||
                result.customerCompanyName ||
                result.customerName ||
                "";
              const custEmail =
                result.recipientEmail || result.customerEmailAddress || "";

              setValues((prev) => ({
                ...prev,
                ...result,
                quotationID: QuotationID,
                quotationNumber: result.quotationNumber || "",
                name: custName,
                recipientEmail: custEmail,
                purchaseOrderNumber: result.purchaseOrderNumber || "",
                quotationDate: result.quotationDate
                  ? new Date(result.quotationDate)
                  : new Date(),
                validUptoDate: result.validUptoDate
                  ? new Date(result.validUptoDate)
                  : new Date(),
                validUpto: result.validUpto || "14 Days",
                gstType: result.gstType || "Inclusive",
                paymentInstruction: result.paymentInstruction || "",
                terms: generateQuotationTerms(result.validUpto || "14 Days"),
                emailSent: result.emailSent || 0,
                createdByName: result.createdByName || userName,
                createdAt:
                  result.createdDate || result.createdAt || prev.createdAt,
              }));

              if (custName) {
                setCustomerSearchQuery(custName);
              }

              pendingItemsScrollRef.current = "top";
              if (result.items?.length) {
                const formattedItems = result.items.map((it) => {
                  const price = parseFloat(it.itemPrice) || 0;
                  const qty = parseFloat(it.itemQuantity) || 1;
                  const dType = it.discountType || "None";
                  const dVal =
                    it.discountValue !== undefined &&
                    it.discountValue !== null &&
                    it.discountValue !== ""
                      ? String(it.discountValue)
                      : "";
                  const dAmt = parseFloat(it.discountAmount) || 0;
                  const total =
                    it.totalItemPrice !== undefined
                      ? it.totalItemPrice
                      : price * qty - dAmt;
                  return {
                    ...EMPTY_ITEM_ROW,
                    ...it,
                    itemID: it.itemID || it._id || "",
                    itemName: it.itemName || "",
                    itemDescription: it.itemDescription || "",
                    itemPrice:
                      it.itemPrice !== undefined && it.itemPrice !== null
                        ? String(it.itemPrice)
                        : "",
                    itemQuantity: qty,
                    categoryCodeName: it.categoryCodeName || "Sales",
                    gst:
                      it.gst !== undefined && it.gst !== null
                        ? Number(it.gst)
                        : 10,
                    discountType: dType,
                    discountValue: dVal,
                    discountAmount: dAmt,
                    totalItemPrice: parseFloat(total || 0).toFixed(2),
                  };
                });
                setItemRowValues(formattedItems);
              } else {
                setItemRowValues([{ ...EMPTY_ITEM_ROW }]);
              }

              if (result.quotationDocuments?.length) {
                setDocuments(result.quotationDocuments);
              } else {
                setDocuments([]);
              }
              setIsSaved(true);
              setIsDirty(false);
            } else {
              showResMsg(
                data?.Result_Message || "Quotation details not found",
                2000,
                false
              );
              navigate("/quotations");
            }
          },
          dispatch,
          () => {
            showResMsg(
              "The quotation could not be loaded. Please try again.",
              2000,
              false
            );
          }
        );
        await Promise.all([supportingRequests, quotationRequest]);
        setIsEditorLoading(false);
      }
    };
    init();
  }, [QuotationID]);

  // Fallback listener for resolving customer info when contactDetails finishes loading
  useEffect(() => {
    if (values.contactID && contactDetails?.length > 0) {
      const matched = contactDetails.find(
        (c) => String(c._id) === String(values.contactID)
      );
      if (matched) {
        const cName = matched.companyName || matched.name || "";
        const cEmail =
          matched.emailAddress ||
          matched.customerEmailAddress ||
          matched.email ||
          "";
        setValues((prev) => ({
          ...prev,
          name: prev.name || cName,
          recipientEmail: prev.recipientEmail || cEmail,
        }));
        setCustomerSearchQuery((prev) => prev || cName);
      }
    }
  }, [contactDetails, values.contactID]);

  // Click outside listener for dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(e.target)
      ) {
        setIsCustomerDropdownOpen(false);
        if (values.name) {
          setCustomerSearchQuery(values.name);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeItemDropdownIdx, values.name]);

  // Date calculation helpers
  const addDays = (date, days) => {
    const res = new Date(date);
    res.setDate(res.getDate() + days);
    return res;
  };

  const endOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const calculateValidUpto = useCallback((term, baseDate) => {
    const cur = new Date(baseDate || new Date());
    cur.setHours(23, 59, 58, 999);
    const t = String(term).toLowerCase();

    if (t.includes("same day")) return cur;
    if (t.includes("7 day")) return addDays(cur, 7);
    if (t.includes("14 day") && t.includes("end of month"))
      return addDays(endOfMonth(cur), 14);
    if (t.includes("14 day")) return addDays(cur, 14);
    if (t.includes("30 day") && t.includes("end of month"))
      return addDays(endOfMonth(cur), 30);
    if (t.includes("30 day")) return addDays(cur, 30);

    return addDays(cur, 14);
  }, []);

  // Update validity date when validUpto or quotationDate changes
  const handleValidityChange = (newTerm) => {
    setIsDirty(true);
    setIsSaved(false);
    const validDate = calculateValidUpto(newTerm, values.quotationDate);
    setValues((prev) => ({
      ...prev,
      validUpto: newTerm,
      validUptoDate: validDate,
      terms: generateQuotationTerms(newTerm),
    }));
  };

  const handleDateChange = (dateVal) => {
    setIsDirty(true);
    setIsSaved(false);
    const newDate = dateVal ? new Date(dateVal) : "";
    const validDate = newDate
      ? calculateValidUpto(values.validUpto, newDate)
      : "";
    setValues((prev) => ({
      ...prev,
      quotationDate: newDate,
      validUptoDate: validDate,
    }));
  };

  // Calculations: Subtotal, Discounts, GST, Final Total
  const calculateTotals = useCallback((rows, gstType) => {
    let subtotalExGst = 0;
    let totalDiscount = 0;
    let totalGst = 0;
    let grandTotal = 0;

    rows.forEach((row) => {
      if (!row.itemName && !row.itemID) return;
      const price = parseFloat(row.itemPrice) || 0;
      const qty = parseFloat(row.itemQuantity) || 0;
      const lineGross = price * qty;
      const gstRate = gstType === "No GST" ? 0 : parseFloat(row.gst) || 10;

      let lineDisc = 0;
      if (row.discountType === "Percentage") {
        lineDisc = (lineGross * (parseFloat(row.discountValue) || 0)) / 100;
      } else if (row.discountType === "Fixed") {
        lineDisc = parseFloat(row.discountValue) || 0;
      }
      lineDisc = Math.min(lineDisc, lineGross);
      totalDiscount += lineDisc;

      const lineNet = Math.max(0, lineGross - lineDisc);

      if (gstType === "Inclusive") {
        // In Australia, 10% GST inclusive means GST = Net / 11
        const lineGstPart = lineNet * (gstRate / (100 + gstRate));
        const lineExGst = lineNet - lineGstPart;
        subtotalExGst += lineExGst;
        totalGst += lineGstPart;
        grandTotal += lineNet;
      } else {
        // GST Exclusive: Net is ex-GST, tax is added
        const lineGstPart = lineNet * (gstRate / 100);
        subtotalExGst += lineNet;
        totalGst += lineGstPart;
        grandTotal += lineNet + lineGstPart;
      }
    });

    return {
      subtotalExGst: subtotalExGst.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      totalGst: totalGst.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
    };
  }, []);

  // Recalculate totals whenever rows or gstType changes
  useEffect(() => {
    const { subtotalExGst, totalDiscount, totalGst, grandTotal } =
      calculateTotals(itemRowValues, values.gstType);
    setValues((prev) => ({
      ...prev,
      totalAmount: subtotalExGst,
      totalDiscountAmount: totalDiscount,
      totalGSTAmount: totalGst,
      finalAmount: grandTotal,
    }));
  }, [itemRowValues, values.gstType, calculateTotals]);

  useEffect(() => {
    const shouldOpenSend =
      new URLSearchParams(window.location.search).get("send") === "true";
    if (
      shouldOpenSend &&
      values.contactID &&
      !isSavingQuote &&
      !openedSendFromPreviewRef.current
    ) {
      openedSendFromPreviewRef.current = true;
      handleOpenSendModal();
    }
  }, [values.contactID, isSavingQuote]);

  useEffect(() => {
    if (!isSendModalOpen || sendRecipient) return;
    const customer = contactDetails.find(
      (contact) => String(contact._id) === String(values.contactID)
    );
    const recipient = String(
      values.recipientEmail ||
        customer?.emailAddress ||
        customer?.customerEmailAddress ||
        customer?.email ||
        ""
    ).trim();
    if (recipient) {
      setSendRecipient(recipient);
      setRecipientEmailError(getRecipientEmailError(recipient, true));
    }
  }, [
    isSendModalOpen,
    sendRecipient,
    values.recipientEmail,
    values.contactID,
    contactDetails,
  ]);

  useEffect(() => {
    if (isSendModalOpen) prepareQuotationEmail();
  }, [isSendModalOpen]);

  const hasPendingAttachments = quoDocuments.some((document) => document.file);
  useEffect(() => {
    const targetId = QuotationID || values.quotationID;
    if (
      !isSendModalOpen ||
      !targetId ||
      isDirty ||
      hasPendingAttachments ||
      sendingEmail
    )
      return undefined;
    const prepared = {
      id: String(targetId),
      snapshot: captureWebsiteDocument("quotation", targetId).catch(() => null),
    };
    sendDocumentRef.current = prepared;
    return () => {
      if (sendDocumentRef.current === prepared) sendDocumentRef.current = null;
    };
  }, [
    isSendModalOpen,
    QuotationID,
    values.quotationID,
    isDirty,
    hasPendingAttachments,
    sendingEmail,
  ]);

  // Customer combobox selection
  const handleSelectCustomer = (contact) => {
    setIsDirty(true);
    setIsSaved(false);
    setContactNameErr(false);
    const cName = contact.companyName || contact.name || "";
    const cEmail =
      contact.emailAddress ||
      contact.customerEmailAddress ||
      contact.email ||
      "";
    setRecipientEmailError(
      getRecipientEmailError(cEmail || values.recipientEmail)
    );
    const pTerm = contact.paymentTerms || "14 Days";
    setValues((prev) => ({
      ...prev,
      contactID: contact._id,
      name: cName,
      recipientEmail: cEmail || prev.recipientEmail,
      validUpto: pTerm,
      validUptoDate: calculateValidUpto(pTerm, prev.quotationDate),
      terms: generateQuotationTerms(pTerm),
    }));
    setCustomerSearchQuery(cName);
    setIsCustomerDropdownOpen(false);
  };

  const handleEditCustomer = async (contact) => {
    if (!canManageCustomers || isCustomerSaving) return;
    setIsCustomerSaving(true);
    try {
      const { data } = await axios.post(
        `${appConstants.baseURL}contact/fetchContactDetailsById`,
        {
          _id: contact._id,
          businessUserID: loginDetails?.businessUserID,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
        }
      );
      const customer = data?.Result_Output?.[0];
      if (!data?.Result_Status || !customer) {
        showResMsg(
          data?.Result_Message || "Unable to load customer.",
          2000,
          false
        );
        return;
      }
      if (customer.businessUserID !== loginDetails?.businessUserID) {
        showResMsg("Unable to load customer.", 2000, false);
        return;
      }
      setEditingCustomer(customer);
      setNewCustomerForm({
        companyName: customer.companyName || "",
        contactPerson: customer.name || "",
        mobile: formatAustralianMobile(String(customer.phoneNumber || "")),
        email: customer.emailAddress || "",
        abn: String(customer.abnNumber || ""),
        paymentTerms: customer.paymentTerms || "14 Days",
        autoReminders: customer.invoiceReminder !== "No",
      });
      setCustomerFieldErrors({});
      setCustomerModalError("");
      setIsCustomerDropdownOpen(false);
      setIsCustomerModalOpen(true);
    } catch (error) {
      showResMsg(
        error?.response?.data?.Result_Message ||
          "Unable to load customer. Please try again.",
        2000,
        false
      );
    } finally {
      setIsCustomerSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!canManageCustomers || !customerToDelete || isCustomerDeleting) return;
    setIsCustomerDeleting(true);
    setCustomerDeleteError("");
    try {
      const { data } = await axios.post(
        `${appConstants.baseURL}contact/deleteContact`,
        {
          _id: [customerToDelete._id],
          businessUserID: loginDetails?.businessUserID,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
        }
      );
      if (!data?.Result_Status) {
        setCustomerDeleteError(
          data?.Result_Message || "Unable to delete customer."
        );
        return;
      }
      setContactDetails((previous) =>
        previous.filter((contact) => contact._id !== customerToDelete._id)
      );
      if (values.contactID === customerToDelete._id) {
        setValues((previous) => ({
          ...previous,
          contactID: "",
          name: "",
          recipientEmail: "",
        }));
        setCustomerSearchQuery("");
        setRecipientEmailError("");
        setIsDirty(true);
        setIsSaved(false);
      }
      setCustomerToDelete(null);
      showResMsg("Customer deleted successfully", 1500, true);
    } catch (error) {
      setCustomerDeleteError(
        error?.response?.data?.Result_Message ||
          "Unable to delete customer. Please try again."
      );
    } finally {
      setIsCustomerDeleting(false);
    }
  };

  // Quick Create / Edit Customer Submit
  const getCustomerFieldError = validateQuotationCustomerField;

  const handleCustomerFieldChange = (field, value) => {
    if (field === "mobile")
      value = formatAustralianMobile(sanitizeAustralianMobileInput(value));
    setNewCustomerForm((previous) => ({ ...previous, [field]: value }));
    if (customerFieldErrors[field]) {
      setCustomerFieldErrors((previous) => ({
        ...previous,
        [field]: getCustomerFieldError(field, value),
      }));
    }
  };

  const handleCustomerFieldBlur = (field, value) => {
    const errors = validateQuotationCustomerForm({
      ...newCustomerForm,
      [field]: value,
    });
    const firstField = Object.keys(errors)[0];
    const error = errors[field];
    setCustomerFieldErrors((previous) =>
      error || previous[firstField] ? errors : {}
    );
    if (!error)
      setNewCustomerForm((previous) => ({
        ...previous,
        [field]: normalizeQuotationCustomerField(field, value),
      }));
  };

  const handleCustomerSaveError = (message, fieldErrors) => {
    if (fieldErrors && typeof fieldErrors === "object") {
      setCustomerFieldErrors((previous) => ({ ...previous, ...fieldErrors }));
      if (fieldErrors.form) setCustomerModalError(fieldErrors.form);
      return;
    }
    const field = /abn/i.test(message)
      ? "abn"
      : /payment terms/i.test(message)
      ? "paymentTerms"
      : /email/i.test(message)
      ? "email"
      : /mobile|phone/i.test(message)
      ? "mobile"
      : /company/i.test(message)
      ? "companyName"
      : /\bname\b/i.test(message)
      ? "contactPerson"
      : null;
    if (field)
      setCustomerFieldErrors((previous) => ({ ...previous, [field]: message }));
    else setCustomerModalError(message);
  };

  const handleQuickCreateCustomer = async () => {
    if (isCustomerSaving) return;
    setCustomerModalError("");
    const errors = validateQuotationCustomerForm(newCustomerForm);
    setCustomerFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    const mobile = formatAustralianMobile(newCustomerForm.mobile).replace(
      /\s/g,
      ""
    );
    setIsCustomerSaving(true);

    const payload = {
      source: "quotation-quick-create",
      businessUserID: loginDetails?.businessUserID,
      companyName: normalizeQuotationCustomerField(
        "companyName",
        newCustomerForm.companyName
      ),
      name: normalizeQuotationCustomerField(
        "contactPerson",
        newCustomerForm.contactPerson
      ),
      phoneNumber: mobile.replace(/^\+/, "").replace(/^61$/, ""),
      emailAddress: normalizeQuotationCustomerField(
        "email",
        newCustomerForm.email
      ),
      abnNumber: newCustomerForm.abn.trim().replace(/[ -]/g, ""),
      paymentTerms: newCustomerForm.paymentTerms,
      contactType: "Customer",
      contactStatus: "Active",
      invoiceReminder: newCustomerForm.autoReminders ? "Yes" : "No",
    };

    try {
      if (editingCustomer) {
        if (!canManageCustomers) return;
        const { source, contactType, contactStatus, ...changes } = payload;
        const { data } = await axios.post(
          `${appConstants.baseURL}contact/updateContact`,
          {
            ...changes,
            _id: editingCustomer._id,
          },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
            },
          }
        );
        if (!data?.Result_Status) {
          handleCustomerSaveError(
            data?.Result_Message || "Failed to update customer.",
            data?.Result_Output?.fieldErrors
          );
          return;
        }
        const updated = { ...editingCustomer, ...changes, ...(data.Result_Output || {}) };
        setContactDetails((previous) =>
          previous.map((contact) =>
            contact._id === updated._id ? updated : contact
          )
        );
        if (values.contactID === updated._id) {
          const updateRecipient =
            !values.recipientEmail ||
            values.recipientEmail === editingCustomer.emailAddress;
          setValues((previous) => ({
            ...previous,
            name: updated.companyName || updated.name,
            recipientEmail: updateRecipient
              ? updated.emailAddress
              : previous.recipientEmail,
          }));
          setCustomerSearchQuery(updated.companyName || updated.name);
          if (updateRecipient) setRecipientEmailError("");
          setIsDirty(true);
          setIsSaved(false);
        }
        setIsCustomerModalOpen(false);
        setEditingCustomer(null);
        showResMsg("Customer updated successfully", 1500, true);
        return;
      }
      await addContactData(
        payload,
        (data) => {
          if (data?.Result_Status && data.Result_Output?._id) {
            showDraftSavedMessage("Customer created successfully");
            const created = data.Result_Output;
            setContactDetails((prev) => [created, ...prev]);
            handleSelectCustomer(created);
            setIsCustomerModalOpen(false);
            setCustomerFieldErrors({});
            setNewCustomerForm({
              companyName: "",
              contactPerson: "",
              mobile: "+61",
              email: "",
              abn: "",
              paymentTerms: "14 Days",
              autoReminders: true,
            });
          } else {
            handleCustomerSaveError(
              data?.Result_Message || "Failed to create customer.",
              data?.Result_Output?.fieldErrors
            );
          }
        },
        dispatch,
        (error) => {
          handleCustomerSaveError(
            error?.Result_Message ||
              "Customer could not be saved. Please try again.",
            error?.Result_Output?.fieldErrors
          );
        }
      );
    } catch (error) {
      handleCustomerSaveError(
        error?.response?.data?.Result_Message ||
          "Customer could not be saved. Please try again.",
        error?.response?.data?.Result_Output?.fieldErrors
      );
    } finally {
      setIsCustomerSaving(false);
    }
  };

  // Line item modification handlers
  const handleItemRowChange = (index, field, value) => {
    if (field === "itemQuantity") {
      value = sanitizeQuantityInputValue(value);
    }
    setIsDirty(true);
    setIsSaved(false);
    setItemRowValues((prev) => {
      const updated = [...prev];
      const curRow = { ...updated[index], [field]: value };

      if (field === "itemName" && value !== updated[index]?.itemName) {
        curRow.itemID = "";
      }

      if (
        field === "itemPrice" ||
        field === "itemQuantity" ||
        field === "discountType" ||
        field === "discountValue"
      ) {
        const p =
          parseFloat(field === "itemPrice" ? value : curRow.itemPrice) || 0;
        const q =
          parseFloat(field === "itemQuantity" ? value : curRow.itemQuantity) ||
          0;
        const gross = p * q;

        let dAmt = 0;
        const dType = field === "discountType" ? value : curRow.discountType;
        const dVal =
          parseFloat(
            field === "discountValue" ? value : curRow.discountValue
          ) || 0;

        if (dType === "Percentage") {
          dAmt = (gross * dVal) / 100;
        } else if (dType === "Fixed") {
          dAmt = dVal;
        }
        curRow.discountAmount = dAmt;
        curRow.totalItemPrice = Math.max(0, gross - dAmt).toFixed(2);
      }

      updated[index] = curRow;
      return updated;
    });
  };

  const getMatchingCatalogueItems = (query = "") => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return masterItemDetails;
    return masterItemDetails.filter(
      (item) =>
        item.itemName?.toLowerCase().includes(normalizedQuery) ||
        item.itemCode?.toLowerCase().includes(normalizedQuery) ||
        item.itemDescription?.toLowerCase().includes(normalizedQuery)
    );
  };

  // Selecting a catalogue item into a row
  const handleSelectCatalogueItem = (index, item) => {
    setIsDirty(true);
    setIsSaved(false);
    setItemRowValues((prev) => {
      const updated = [...prev];
      const price = parseFloat(item.itemPrice) || 0;
      const qty = updated[index]?.itemQuantity || 1;
      const dType = item.discountType || "None";
      const dVal =
        item.discountValue !== undefined && item.discountValue !== ""
          ? String(item.discountValue)
          : "";
      let dAmt = 0;
      if (dType === "Percentage") {
        dAmt = (price * qty * (parseFloat(dVal) || 0)) / 100;
      } else if (dType === "Fixed") {
        dAmt = parseFloat(dVal) || 0;
      }
      const gross = price * qty;
      const total = Math.max(0, gross - dAmt);

      updated[index] = {
        ...updated[index],
        itemID: item._id,
        itemName: item.itemName,
        itemDescription: item.itemDescription || "",
        categoryCodeName: item.categoryCodeName || "Sales",
        itemPrice: price.toFixed(2),
        itemQuantity: qty,
        gst: item.gst !== undefined ? item.gst : 10,
        discountType: dType,
        discountValue: dVal,
        discountAmount: dAmt,
        totalItemPrice: total.toFixed(2),
      };
      return updated;
    });
    setItemSearchQueries((prev) => ({ ...prev, [index]: undefined }));
    setActiveItemDropdownIdx(null);
  };

  const handleCatalogueItemUpdated = (savedItem) => {
    const result = Array.isArray(savedItem) ? savedItem[0] : savedItem;
    const current = editingCatalogueItem?.item;
    if (!current || !result) return;
    const updatedItem = {
      ...current,
      ...result,
      _id: result._id || current._id,
    };
    setMasterItemDetails((items) =>
      items.map((item) =>
        String(item._id) === String(updatedItem._id) ? updatedItem : item
      )
    );
    handleSelectCatalogueItem(editingCatalogueItem.rowIndex, updatedItem);
    setEditingCatalogueItem(null);
    showResMsg("Item updated successfully", 1500, true);
  };

  // Add line item row
  const handleAddLineRow = () => {
    setIsDirty(true);
    setIsSaved(false);
    setActiveItemDropdownIdx(null);
    pendingItemsScrollRef.current = "bottom";
    setItemRowValues((prev) => [...prev, { ...EMPTY_ITEM_ROW }]);
  };

  const handleMoveLineRow = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= itemRowValues.length)
      return;
    setItemRowValues((rows) => {
      const reordered = [...rows];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      return reordered;
    });
    setItemSearchQueries((queries) => {
      const indices = itemRowValues.map((_, index) => index);
      const [moved] = indices.splice(fromIndex, 1);
      indices.splice(toIndex, 0, moved);
      return Object.fromEntries(
        indices.map((oldIndex, newIndex) => [newIndex, queries[oldIndex]])
      );
    });
    setActiveItemDropdownIdx(null);
    setIsDirty(true);
    setIsSaved(false);
    window.requestAnimationFrame(() => {
      document
        .getElementById(`quotation-item-move-${toIndex}`)
        ?.focus({ preventScroll: true });
    });
  };

  const resetItemDrag = () => {
    if (itemDragRef.current?.dropTimer)
      window.clearTimeout(itemDragRef.current.dropTimer);
    itemDragRef.current?.layer?.remove();
    itemDragRef.current = null;
    setDraggedItemIndex(null);
    setDropTargetIndex(null);
  };

  const handleItemDragStart = (event, index) => {
    if (event.button !== 0 || isSavingQuote || itemRowValues.length < 2) return;
    const tableBody = itemsTableBodyRef.current;
    const source = event.currentTarget.closest("[data-quotation-item-index]");
    if (!source) return;
    const sourceBounds = source.getBoundingClientRect();
    const layer = document.createElement("div");
    layer.className = `quotation-editor-page quotation-item-drag-layer${
      darkMode ? " dark" : ""
    }`;
    layer.dataset.theme = darkMode ? "dark" : "light";
    layer.setAttribute("aria-hidden", "true");
    const preview = source.cloneNode(true);
    preview.classList.add("quotation-item-drag-preview", "is-dragging");
    preview.classList.remove("drop-before", "drop-after");
    preview.removeAttribute("data-quotation-item-index");
    preview
      .querySelectorAll(
        ".quotation-item-search-dropdown, .quotation-figma-dropdown-menu"
      )
      .forEach((element) => element.remove());
    preview
      .querySelectorAll("[id]")
      .forEach((element) => element.removeAttribute("id"));
    preview
      .querySelectorAll("input, button, select, textarea, [tabindex]")
      .forEach((element) => element.setAttribute("tabindex", "-1"));
    preview.style.left = `${sourceBounds.left}px`;
    preview.style.top = `${sourceBounds.top}px`;
    preview.style.width = `${sourceBounds.width}px`;
    preview.style.height = `${sourceBounds.height}px`;
    layer.appendChild(preview);
    document.body.appendChild(layer);
    itemDragRef.current = {
      from: index,
      to: index,
      pointerID: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      source,
      sourceBounds,
      rowHeight: sourceBounds.height,
      layer,
      preview,
      active: false,
      dropping: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setActiveItemDropdownIdx(null);
    setDraggedItemIndex(index);
    setDropTargetIndex(index);
  };

  const handleItemDragMove = (event) => {
    const drag = itemDragRef.current;
    if (!drag || drag.pointerID !== event.pointerId) return;
    if (
      !drag.active &&
      Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5
    )
      return;
    drag.active = true;
    const tableBody = itemsTableBodyRef.current;
    drag.preview?.style.setProperty(
      "--quotation-drag-offset-x",
      `${event.clientX - drag.startX}px`
    );
    drag.preview?.style.setProperty(
      "--quotation-drag-offset-y",
      `${event.clientY - drag.startY}px`
    );

    const candidates = tableBody
      ? Array.from(tableBody.querySelectorAll("[data-quotation-item-index]"))
      : [];
    if (candidates.length) {
      const previewCenterY =
        drag.sourceBounds.top +
        (event.clientY - drag.startY) +
        drag.rowHeight / 2;
      const target = candidates.reduce(
        (nearest, item) => {
          const itemIndex = Number(item.dataset.quotationItemIndex);
          const currentShift =
            drag.from < drag.to && itemIndex > drag.from && itemIndex <= drag.to
              ? -drag.rowHeight
              : drag.from > drag.to &&
                itemIndex >= drag.to &&
                itemIndex < drag.from
              ? drag.rowHeight
              : 0;
          const bounds = item.getBoundingClientRect();
          const centerY = bounds.top - currentShift + bounds.height / 2;
          const distance = Math.abs(previewCenterY - centerY);
          return distance < nearest.distance ? { item, distance } : nearest;
        },
        { item: candidates[0], distance: Number.POSITIVE_INFINITY }
      ).item;
      const targetIndex = Number(target.dataset.quotationItemIndex);
      if (targetIndex !== drag.to) {
        drag.to = targetIndex;
        setDropTargetIndex(targetIndex);
      }
    }

    if (tableBody?.classList.contains("is-scrollable")) {
      const bounds = tableBody.getBoundingClientRect();
      const edgeSize = Math.min(64, bounds.height / 3);
      if (event.clientY < bounds.top + edgeSize) tableBody.scrollTop -= 18;
      else if (event.clientY > bounds.bottom - edgeSize)
        tableBody.scrollTop += 18;
    }
    if (event.clientY < 64) window.scrollBy(0, -20);
    else if (event.clientY > window.innerHeight - 64) window.scrollBy(0, 20);
  };

  const handleItemDragEnd = (event) => {
    const drag = itemDragRef.current;
    const shouldAnimateDrop = Boolean(
      drag &&
        drag.pointerID === event.pointerId &&
        drag.active &&
        drag.from !== drag.to
    );
    if (shouldAnimateDrop) drag.dropping = true;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (!shouldAnimateDrop) {
      resetItemDrag();
      return;
    }

    const tableBody = itemsTableBodyRef.current;
    const target = tableBody?.querySelector(
      `[data-quotation-item-index="${drag.to}"]`
    );
    if (!tableBody || !target || !drag.preview) {
      const { from, to } = drag;
      resetItemDrag();
      handleMoveLineRow(from, to);
      return;
    }

    const targetBounds = target.getBoundingClientRect();
    const targetShift = drag.to > drag.from ? -drag.rowHeight : drag.rowHeight;
    const targetTop = targetBounds.top - targetShift;
    const destinationTop =
      drag.to > drag.from
        ? targetTop + target.offsetHeight - drag.rowHeight
        : targetTop;
    drag.preview.style.transition =
      "transform 170ms cubic-bezier(0.2, 0, 0, 1), box-shadow 170ms ease";
    drag.preview.getBoundingClientRect();
    drag.preview.style.setProperty("--quotation-drag-offset-x", "0px");
    drag.preview.style.setProperty(
      "--quotation-drag-offset-y",
      `${destinationTop - drag.sourceBounds.top}px`
    );
    drag.dropTimer = window.setTimeout(() => {
      const { from, to } = drag;
      resetItemDrag();
      handleMoveLineRow(from, to);
    }, 175);
  };

  const handleItemDragLostCapture = () => {
    if (!itemDragRef.current?.dropping) resetItemDrag();
  };

  const getItemRowDragStyle = (index) => {
    if (
      draggedItemIndex === null ||
      dropTargetIndex === null ||
      index === draggedItemIndex
    )
      return undefined;
    const rowHeight = itemDragRef.current?.rowHeight || 0;
    if (
      draggedItemIndex < dropTargetIndex &&
      index > draggedItemIndex &&
      index <= dropTargetIndex
    ) {
      return { "--quotation-row-shift-y": `${-rowHeight}px` };
    }
    if (
      draggedItemIndex > dropTargetIndex &&
      index >= dropTargetIndex &&
      index < draggedItemIndex
    ) {
      return { "--quotation-row-shift-y": `${rowHeight}px` };
    }
    return undefined;
  };

  // Remove line item row
  const handleRemoveLineRow = (index) => {
    ShowSwalWithButtons(
      "Are you sure?",
      `Would you like to remove the Item?`,
      "warning",
      darkMode,
      () => {
        setIsDirty(true);
        setIsSaved(false);
        const itemError = draftValidationField?.match(
          /^(quotation-item(?:-search|Price|Quantity)-)(\d+)$/
        );
        if (itemError && Number(itemError[2]) >= index) {
          const errorIndex = Number(itemError[2]);
          setDraftValidationField(
            errorIndex === index ? null : `${itemError[1]}${errorIndex - 1}`
          );
        }
        setItemRowValues((currentRows) =>
          currentRows.length <= 1
            ? [{ ...EMPTY_ITEM_ROW }]
            : currentRows.filter((_, rowIndex) => rowIndex !== index)
        );
        setItemSearchQueries({});
        setActiveItemDropdownIdx(null);
      }
    );
  };

  const handleOpenNewItem = (targetIndex = null, itemName = "") => {
    setActiveItemDropdownIdx(null);
    setItemModalTargetIdx(targetIndex);
    setItemModalError("");
    setNewItemForm({
      itemName,
      itemCode: "",
      itemDescription: "",
      itemPrice: "",
      categoryCodeName: "200 - Sales",
      status: "Active",
      unit: "each",
      defaultQuantity: 1,
      gst: 10,
      discountType: "None",
      discountValue: "",
    });
    setIsItemModalOpen(true);
  };

  // Quick Create Catalogue Item Submit
  const handleQuickCreateItem = async () => {
    if (isItemSaving) return;
    if (!newItemForm.itemCode.trim()) {
      setItemModalError("itemCode");
      return;
    }
    if (!newItemForm.itemName.trim()) {
      setItemModalError("itemName");
      return;
    }
    if (newItemForm.itemPrice === "") {
      setItemModalError("itemPrice");
      return;
    }
    setItemModalError("");
    setIsItemSaving(true);

    const payload = {
      businessUserID: loginDetails?.businessUserID,
      itemName: newItemForm.itemName.trim(),
      itemCode: newItemForm.itemCode.trim(),
      itemDescription: newItemForm.itemDescription.trim(),
      itemPrice: parseFloat(newItemForm.itemPrice) || 0,
      itemQuantity: parseFloat(newItemForm.defaultQuantity) || 1,
      categoryCodeName: newItemForm.categoryCodeName || "Sales",
      categoryName: newItemForm.categoryCodeName || "Sales",
      gst: newItemForm.gst !== undefined ? parseFloat(newItemForm.gst) : 10,
      itemStatus: newItemForm.status || "Active",
      unit: newItemForm.unit || "each",
      discountType: newItemForm.discountType || "None",
      discountValue: parseFloat(newItemForm.discountValue) || 0,
      source: "quotation",
    };

    await addCatalogueItemData(
      payload,
      (data) => {
        if (data?.Result_Status && data.Result_Output) {
          showDraftSavedMessage("Catalogue item created successfully");
          const created = data.Result_Output;
          setMasterItemDetails((prev) => [created, ...prev]);
          setIsDirty(true);
          setIsSaved(false);

          // Assign the created item to the row that opened the modal.
          if (itemModalTargetIdx !== null) {
            handleSelectCatalogueItem(itemModalTargetIdx, created);
          } else {
            setItemRowValues((prev) => {
              const createdRow = {
                ...EMPTY_ITEM_ROW,
                itemID: created._id,
                itemName: created.itemName,
                itemDescription: created.itemDescription,
                itemPrice: parseFloat(created.itemPrice || 0).toFixed(2),
                itemQuantity:
                  parseFloat(
                    created.itemQuantity || newItemForm.defaultQuantity
                  ) || 1,
                totalItemPrice: (
                  (parseFloat(created.itemPrice) || 0) *
                  (parseFloat(
                    created.itemQuantity || newItemForm.defaultQuantity
                  ) || 1)
                ).toFixed(2),
                gst: created.gst ?? 10,
              };
              const emptyIndex = prev.findIndex(
                (row) =>
                  !row.itemID &&
                  !String(row.itemName || "").trim() &&
                  !String(row.itemDescription || "").trim()
              );
              if (emptyIndex === -1) return [...prev, createdRow];
              return prev.map((row, index) =>
                index === emptyIndex ? createdRow : row
              );
            });
            setItemSearchQueries({});
          }

          setItemModalTargetIdx(null);
          setIsItemModalOpen(false);
          setNewItemForm({
            itemName: "",
            itemCode: "",
            itemDescription: "",
            itemPrice: "",
            categoryCodeName: "200 - Sales",
            status: "Active",
            unit: "each",
            defaultQuantity: 1,
            gst: 10,
            discountType: "None",
            discountValue: "",
          });
        } else {
          const message =
            data?.Result_Message || "Failed to create catalogue item.";
          setItemModalError(message);
          showResMsg(message, 2000, false);
        }
      },
      dispatch,
      { showLoader: false }
    );
    setIsItemSaving(false);
  };

  // Document attachment upload
  const updateUploadedDocuments = (uploaded, pendingID) => {
    setDocuments((previous) => [
      ...uploaded,
      ...previous.filter(
        (document) => document.file && document._id !== pendingID
      ),
    ]);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      setFileError("File size must be less than 1 MB");
      return;
    }
    setFileError("");
    const document = createPendingAttachment(file);
    setDocuments((previous) => [...previous, document]);
    setIsDirty(true);
    setIsSaved(false);
    const quotationID = values.quotationID || QuotationID;
    if (!quotationID) return;
    dispatch(loadingAction(true));

    try {
      await uploadPendingAttachments(
        "quotation",
        quotationID,
        [document],
        updateUploadedDocuments
      );
      showResMsg("Document uploaded successfully", 1500, true);
    } catch (err) {
      setFileError(`${err.message} Save the draft to retry.`);
    } finally {
      dispatch(loadingAction(false));
    }
  };

  // Delete attachment
  const handleDeleteDocument = (doc) => {
    ShowSwalWithButtons(
      "Remove attachment?",
      `Are you sure you want to delete "${doc.documentName}"?`,
      "warning",
      darkMode,
      async () => {
        if (doc.file) {
          setDocuments((previous) =>
            previous.filter((document) => document._id !== doc._id)
          );
          setFileError("");
          setIsDirty(true);
          return;
        }
        try {
          const res = await axios.post(
            `${appConstants.baseURL}quotation/deleteQuotationDocument`,
            { documentID: doc._id },
            {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
              },
            }
          );
          if (res.data?.Result_Status) {
            showResMsg("Document deleted", 1500, true);
            setDocuments((prev) => prev.filter((d) => d._id !== doc._id));
            setIsDirty(true);
          } else {
            showResMsg(
              res.data?.Result_Message || "Delete failed",
              2000,
              false
            );
          }
        } catch (err) {
          console.error(err);
          showResMsg("Error deleting document", 2000, false);
        }
      }
    );
  };

  // View attachment
  const handleViewDocument = (doc) => {
    setSelectedFileType(doc.fileType);
    const url = doc.file
      ? URL.createObjectURL(doc.file)
      : `${appConstants.baseURL}quotation/viewUploadedQuotationDocument?fileName=/${doc.documentFileName}`;
    setDocumentFileURL(url);
    setViewDocumentInModal(true);
  };

  const hasSavedQuotation = Boolean(
    (QuotationID || values.quotationID) &&
      values.quotationNumber &&
      values.quotationNumber !== "Auto Generated"
  );
  const canUseDocumentActions =
    hasSavedQuotation &&
    !isDirty &&
    !isSavingQuote &&
    !quoDocuments.some((document) => document.file);

  const validateQuotationDraft = () => {
    setDraftValidationAttempted(true);
    const customerMissing = !values.contactID;
    setContactNameErr(customerMissing);
    if (customerMissing) setIsCustomerDropdownOpen(true);
    const emailError = values.contactID
      ? getRecipientEmailError(values.recipientEmail, true)
      : "";
    setRecipientEmailError(emailError);
    const invalidItemIndex = allDraftItemErrors.findIndex(
      (errors) => Object.keys(errors).length
    );
    if (
      customerMissing ||
      emailError ||
      draftDateError ||
      invalidItemIndex >= 0
    ) {
      const itemField =
        invalidItemIndex >= 0
          ? Object.keys(allDraftItemErrors[invalidItemIndex])[0]
          : "";
      const itemInputID =
        itemField === "itemName"
          ? `quotation-item-search-${invalidItemIndex}`
          : `quotation-${itemField}-${invalidItemIndex}`;
      const firstInvalidID = customerMissing
        ? "quotation-customer-input"
        : emailError
        ? "quotation-recipient-email"
        : draftDateError
        ? "quotation-date-input"
        : itemInputID;
      setDraftValidationField(firstInvalidID);
      window.requestAnimationFrame(() => {
        const input = document.getElementById(firstInvalidID);
        input?.focus({ preventScroll: true });
        input?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      if (isSendModalOpen) setIsSendModalOpen(false);
      return false;
    }

    setDraftValidationField(null);
    return true;
  };

  // Save / Update Quotation Draft
  const handleSaveQuotation = async (
    onSuccess,
    { redirectAfterSave = true, approve = false } = {}
  ) => {
    if (isSavingQuote) return;
    if (!approve && canUseDocumentActions) {
      showDraftSavedMessage("Draft is already saved.");
      if (typeof onSuccess === "function") {
        await onSuccess(
          QuotationID || values.quotationID,
          values.quotationNumber
        );
      }
      return;
    }
    if (!validateQuotationDraft()) return;
    const reportError = (message) => {
      if (isSendModalOpen) setSendError(message);
      showResMsg(message, 2000, false);
    };
    const filledItems = itemRowValues
      .filter(
        (row) =>
          String(row.itemName || "").trim() !== "" ||
          String(row.itemDescription || "").trim() !== ""
      )
      .map((row) => ({
        ...row,
        itemName: String(row.itemName || "").trim(),
        itemDescription: String(row.itemDescription || "").trim(),
        itemID: row.itemID && row.itemID !== "" ? row.itemID : undefined,
      }));

    if (filledItems.length === 0) {
      reportError("Enter an item name or description.");
      return;
    }

    setIsSavingQuote(true);
    setSendError("");

    const payload = {
      ...values,
      quotationID: QuotationID || values.quotationID,
      quotationNumber: String(values.quotationNumber || "").trim(),
      quotationNumberEdited: quoteNumberEditedRef.current,
      revisionRequested: revisionPendingRef.current,
      sendEmailFlag: false,
      recipientEmail: String(values.recipientEmail || "").trim(),
      businessUserID: loginDetails?.businessUserID,
      userID: loginDetails?._id,
      token: undefined,
      currencyValue: currencyCode || "AUD",
      itemsList: filledItems,
      quotationDate: new Date(values.quotationDate),
      validUptoDate: new Date(values.validUptoDate),
      hasQuo_DocumentFile: quoDocuments.length > 0,
      ...(approve
        ? { quotationStatus: "Approved", activityStatus: "Approved" }
        : {}),
    };

    const isUpdate = Boolean(QuotationID || values.quotationID);
    const apiCall = isUpdate ? UpdateQuotation : SaveQuotation;

    try {
      return await apiCall(
        payload,
        async (data) => {
          if (data?.Result_Status) {
            clearQuotationNumberPreview(loginDetails?.businessUserID);
            numberValidation.clearError();
            const newId =
              data.Result_Output?._id || QuotationID || values.quotationID;
            const quotationNumber =
              data.Result_Output?.quotationNumber || values.quotationNumber;
            setValues((previous) => ({
              ...previous,
              quotationID: newId,
              quotationNumber,
              quotationStatus:
                data.Result_Output?.quotationStatus || payload.quotationStatus,
            }));
            try {
              await uploadPendingAttachments(
                "quotation",
                newId,
                quoDocuments,
                updateUploadedDocuments
              );
              setFileError("");
            } catch (err) {
              const message = `Quotation saved, but ${err.message} Save again to retry the attachment.`;
              setFileError(message);
              reportError(message);
              return;
            }
            if (approve) {
              showResMsg(
                data.Result_Message ||
                  (isUpdate ? "Quotation updated" : "Quotation saved"),
                1800,
                true
              );
            } else {
              showDraftSavedMessage();
            }
            setIsDirty(false);
            setIsSaved(true);
            revisionPendingRef.current = false;
            unsavedChanges.markSaved();
            if (isUpdate) {
              const savedActivity = data.Result_Output?.activity;
              if (savedActivity) {
                setHistoryRows((current) => [
                  { ...savedActivity, name: userName },
                  ...current.filter((row) => row._id !== savedActivity._id),
                ]);
              } else {
                fetchQuotationHistory(newId);
              }
            }

            if (redirectAfterSave && !QuotationID && newId) {
              savedQuotationRef.current = String(newId);
              navigate(`/quotations/edit/${newId}`, { replace: true });
            }
            if (typeof onSuccess === "function") {
              await onSuccess(newId, quotationNumber);
            }
          } else {
            if (numberValidation.handleResponse(data)) {
              setIsSendModalOpen(false);
              setSendError("");
              return;
            }
            if (data?.Result_Output === "NO AVAILABLE CREDITS") {
              setShowBillingModal(true);
            }
            reportError(data?.Result_Message || "Failed to save quotation");
          }
        },
        dispatch,
        { showLoader: false }
      );
    } finally {
      setIsSavingQuote(false);
    }
  };
  saveAndExitRef.current = async () => {
    let saved = false;
    await handleSaveQuotation(
      () => {
        saved = true;
      },
      { redirectAfterSave: false }
    );
    return saved;
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    const targetId = QuotationID || values.quotationID;
    if (!canUseDocumentActions) {
      showResMsg("Save draft first.", 2000, false);
      return;
    }

    dispatch(loadingAction(true));
    try {
      await downloadQuotation(
        { _id: targetId },
        (res) => {
          dispatch(loadingAction(false));
          const pdfContent = res.data;
          if (!pdfContent) {
            showResMsg("PDF generation failed.", 2000, false);
            return;
          }
          const binaryPdfData = atob(pdfContent);
          const arrayBuffer = new ArrayBuffer(binaryPdfData.length);
          const uint8Array = new Uint8Array(arrayBuffer);
          for (let i = 0; i < binaryPdfData.length; i++) {
            uint8Array[i] = binaryPdfData.charCodeAt(i);
          }
          const blob = new Blob([arrayBuffer], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `quote_${values.quotationNumber || targetId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          showResMsg("PDF downloaded successfully", 1500, true);
        },
        dispatch
      );
    } catch (err) {
      dispatch(loadingAction(false));
      console.error(err);
      showResMsg("Error downloading PDF", 2000, false);
    }
  };

  // Preview quote
  const handlePreviewQuote = () => {
    const targetId = QuotationID || values.quotationID;
    if (!canUseDocumentActions) {
      showResMsg("Save draft first.", 2000, false);
      return;
    }
    navigate(`/quotations/quotation/${values.quotationNumber}/${targetId}`, {
      state: {
        previewReturn: {
          path: `${location.pathname}${location.search}`,
          label:
            type === "clone" ? "Back to Clone quote" : "Back to Edit quote",
        },
        previewSnapshot: {
          values: {
            ...values,
            _id: targetId,
            quotationID: targetId,
            itemsList: itemRowValues,
          },
          items: itemRowValues,
        },
      },
    });
  };

  // Send Quote triggers
  const handleOpenSendModal = () => {
    if (isSavingQuote || sendingEmail) return;
    feedbackBus.emit({ scope: "global", message: "", duration: 0 });
    if (!values.contactID) {
      setContactNameErr(true);
      setIsCustomerDropdownOpen(true);
      document.getElementById("quotation-customer-input")?.focus();
      return;
    }
    if (!validateQuotationDraft()) return;
    const customer = contactDetails.find(
      (contact) => String(contact._id) === String(values.contactID)
    );
    const recipient = String(
      values.recipientEmail ||
        customer?.emailAddress ||
        customer?.customerEmailAddress ||
        customer?.email ||
        ""
    ).trim();
    const emailError = getRecipientEmailError(recipient, true);
    setRecipientEmailError(emailError);
    const qNum = values.quotationNumber || "New Quote";
    const amount = formatCurrencyAUD(parseFloat(values.finalAmount) || 0);
    setSendRecipient(recipient);
    setAdditionalRecipients("");
    setAdditionalRecipientsError("");
    setSendSubject(`Quote ${qNum} from ${organisation} · $${amount} AUD`);
    setSendError("");
    setIsSendModalOpen(true);
  };

  // Submit Send Quote
  const handleSendQuoteSubmit = async () => {
    if (sendingEmail || isSavingQuote) return;
    if (!validateQuotationDraft()) return;
    const emailError = getRecipientEmailError(sendRecipient, true);
    const additionalError =
      validateAdditionalQuotationEmails(additionalRecipients);
    setAdditionalRecipientsError(additionalError);
    if (emailError || additionalError) {
      setSendError(emailError);
      if (additionalError)
        document.getElementById("editor-send-recipient")?.focus();
      return;
    }
    setSendingEmail(true);
    setSendError("");
    try {
      let targetId = QuotationID || values.quotationID;
      let prepared = sendDocumentRef.current;
      if (
        isDirty ||
        !targetId ||
        quoDocuments.some((document) => document.file)
      ) {
        prepared = null;
        let savedId;
        await handleSaveQuotation(
          (id) => {
            savedId = id;
          },
          { redirectAfterSave: false }
        );
        if (!savedId) return;
        targetId = savedId;
      }
      const documentPayload = await prepareQuotationEmailDocument(
        targetId,
        prepared?.id === String(targetId) ? prepared.snapshot : undefined
      );
      const response = await axios.post(
        `${appConstants.baseURL}quotation/emailReminderQuotation`,
        {
          quotationID: targetId,
          userID: loginDetails._id,
          businessUserID: loginDetails.businessUserID,
          recipientEmail: sendRecipient.trim(),
          additionalRecipientEmails:
            parseAdditionalQuotationEmails(additionalRecipients),
          emailSubject: sendSubject.trim(),
          attachPdf,
          sendCopy,
          ...documentPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 60000,
        }
      );
      if (response.data?.Result_Status) {
        setIsSendModalOpen(false);
        setSentQuotationId(String(targetId));
        setValues((prev) => ({
          ...prev,
          emailSent: (prev.emailSent || 0) + 1,
          quotationStatus:
            prev.quotationStatus === "Approved" ? "Approved" : "Sent",
        }));
      } else {
        setSendError(response.data?.Result_Message || "Failed to send quote");
        if (response.data?.Result_Output?.partiallySent) {
          setValues((previous) => ({
            ...previous,
            emailSent: response.data.Result_Output.emailSent,
            quotationStatus:
              previous.quotationStatus === "Approved" ? "Approved" : "Sent",
          }));
        }
      }
    } catch (err) {
      console.error(err);
      setSendError(
        err?.response?.data?.Result_Message ||
          "The quote could not be sent. Check the recipient and try again."
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Back button guard
  const handleBack = () => {
    navigate("/quotations");
  };

  // Filtered customer options
  const filteredCustomers = (() => {
    const q = customerSearchQuery.trim().toLowerCase();
    if (!q || q === (values.name || "").trim().toLowerCase())
      return contactDetails;
    return contactDetails.filter(
      (c) =>
        (c.companyName && c.companyName.toLowerCase().includes(q)) ||
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.emailAddress && c.emailAddress.toLowerCase().includes(q)) ||
        (c.customerEmailAddress &&
          c.customerEmailAddress.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q))
    );
  })();

  // Total item count
  const filledItemCount = (() => {
    return itemRowValues.filter((r) => r.itemName && r.itemName.trim() !== "")
      .length;
  })();

  // Normalized currency code (e.g. AUD instead of raw '$')
  const displayCurrencyCode = (() => {
    if (!currencyCode || currencyCode === "$") return "AUD";
    return currencyCode;
  })();

  // Formatted date string for header
  const formattedCreationDate = (() => {
    const d = new Date(values.createdAt || Date.now());
    return d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  })();

  const fetchQuotationHistory = (quotationId = QuotationID || values.quotationID) => {
    if (!quotationId) return;
    fetchActivityLog(
      {
        _id: quotationId,
        businessUserID: loginDetails?.businessUserID,
        userID: loginDetails?._id,
        token: loginDetails?.token,
        screenType: "Quotation",
        page: 1,
        limit: 100,
        perPage: 100,
        sortingType: -1,
        sortingField: "dateAndTime",
        invoiceStatus: "All",
        filterValues: "",
        searchParameterList: ["dateAndTime", "name", "action", "details"],
      },
      (data) => setHistoryRows(data?.Result_Output?.docs || []),
      dispatch
    );
  };

  useEffect(() => {
    const savedQuotationId = QuotationID || values.quotationID;
    if (savedQuotationId) fetchQuotationHistory(savedQuotationId);
  }, [QuotationID, values.quotationID]);

  const handleViewFullHistory = () => {
    if (!QuotationID) return;
    navigate(`/quotations/activityLog/${QuotationID}`, {
      state: {
        previewReturn: {
          path: `${location.pathname}${location.search}`,
          label:
            type === "clone" ? "Back to Clone quote" : "Back to Edit quote",
        },
      },
    });
  };

  const handleAddInternalNote = () => {
    const note = internalNote.trim();
    if (!note) return;
    saveNotes(
      {
        _id: QuotationID,
        businessUserID: loginDetails?.businessUserID,
        userID: loginDetails?._id,
        token: loginDetails?.token,
        details: note,
        screenType: "Quotation",
      },
      (data) => {
        if (data?.Result_Status) {
          setInternalNote("");
          fetchQuotationHistory();
          showResMsg("Internal note added", 1200, true);
        } else {
          showResMsg(data?.Result_Message || "Unable to add note", 2000, false);
        }
      },
      dispatch
    );
  };

  const initials = (name) => {
    if (!name) return "ME";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  if (isEditorLoading) {
    return (
      <div
        className={`dashboard-shell quotation-editor-shell${
          darkMode ? " dark" : ""
        }`}
      >
        <DashboardNavigation
          open={navOpen}
          onClose={() => setNavOpen(false)}
          activePath="/quotations"
          organisation={organisation}
          role={role}
        />
        <div className="dashboard-main quotation-editor-main-area">
          <AppHeader onOpenNav={() => setNavOpen(true)} userName={userName} />
          <main
            className={`quotation-editor-content quotation-editor-page${
              darkMode ? " dark" : ""
            }`}
          >
            <PageSkeleton
              type="editor"
              variant="document"
              label="Loading quotation editor"
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`dashboard-shell quotation-editor-shell${
        darkMode ? " dark" : ""
      }`}
    >
      {/* SIDEBAR NAVIGATION */}
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/quotations"
        organisation={organisation}
        role={role}
      />

      <div className="dashboard-main quotation-editor-main-area">
        {/* FIXED UNTOUCHABLE APP HEADER (Figma Node 429:22151) */}
        <AppHeader onOpenNav={() => setNavOpen(true)} userName={userName} />

        {/* MAIN EDITOR CONTENT */}
        <main
          className={`quotation-editor-content quotation-editor-page${
            darkMode ? " dark" : ""
          }`}
        >
          <InlineFormMessage scope="global" />
          <div className="quotation-editor-header">
            <div data-draft-save-confirmation />
            {/* Back link */}
            <button
              type="button"
              className="quotation-back-link"
              onClick={handleBack}
            >
              <LuArrowLeft />
              <span>Back to Quotations</span>
            </button>

            {/* Title & Actions row */}
            <div className="quotation-editor-title-row">
              <div className="quotation-editor-identity">
                <h1>
                  {QuotationID
                    ? type === "clone"
                      ? "Clone quote"
                      : "Edit quote"
                    : "New quote"}
                </h1>
                <div className="quotation-editor-meta-row">
                  <span className="quotation-meta-docno">
                    {values.quotationNumber || "New quote"}
                  </span>
                  <span className="quotation-draft-status-pill">Draft</span>
                  {isSaved && canUseDocumentActions ? (
                    <div
                      className="quotation-save-indicator"
                      role="status"
                      aria-live="polite"
                    >
                      <LuCheck />
                      <span>Saved</span>
                    </div>
                  ) : isDirty ? (
                    <div
                      className="quotation-save-indicator unsaved"
                      role="status"
                      aria-live="polite"
                    >
                      <i aria-hidden="true" />
                      <span>Unsaved changes</span>
                    </div>
                  ) : null}

                  <span className="quotation-meta-notsent">
                    {values.emailSent > 0
                      ? "Sent"
                      : "Not sent \u00b7 No active expiry"}
                  </span>

                  <span className="quotation-meta-attribution">
                    <span>{organisation}</span>
                    <span aria-hidden="true">{"\u00b7"}</span>
                    <span>All amounts in {displayCurrencyCode}</span>
                    <span aria-hidden="true">{"\u00b7"}</span>
                    <span>Created by {values.createdByName || userName}</span>
                    <span aria-hidden="true">{"\u00b7"}</span>
                    <time>{formattedCreationDate}</time>
                  </span>

                </div>
              </div>

              {/* Draft action buttons */}
              <div className="quotation-header-actions">
                <button
                  type="button"
                  className="quotation-save-draft-action-btn"
                  disabled={isSavingQuote || sendingEmail}
                  aria-label="Save draft"
                  aria-busy={isSavingQuote || undefined}
                  onClick={() => handleSaveQuotation()}
                >
                  <LuSave aria-hidden="true" />
                  <span>Save draft</span>
                  {isSavingQuote && (
                    <CircularProgress
                      className="quotation-save-draft-spinner"
                      color="inherit"
                      size={16}
                      thickness={5}
                      aria-hidden="true"
                    />
                  )}
                </button>
                <button
                  type="button"
                  className="quotation-send-quote-action-btn"
                  disabled={isSavingQuote || sendingEmail}
                  onClick={handleOpenSendModal}
                >
                  <LuSend />
                  <span>Send quote</span>
                </button>
              </div>
            </div>

            <div className="quotation-editor-header-details">
              <p className="quotation-editor-subtitle">
                <strong>{organisation}</strong>
                <span aria-hidden="true">·</span>
                <span>All amounts in {displayCurrencyCode}</span>
              </p>
            </div>
          </div>

          {/* TWO-COLUMN EDITOR LAYOUT */}
          <div className="quotation-editor-layout">
            {/* LEFT COLUMN: FORM CARDS */}
            <div className="quotation-editor-main">
              {/* CARD 1: CUSTOMER & QUOTE SETTINGS */}
              <section className="quotation-editor-card">
                <div className="quotation-card-header">
                  <h3>Customer &amp; quote settings</h3>
                  <p>Choose who this quote is for and set its validity.</p>
                </div>

                <div className="quotation-card-body">
                  {/* Row 1: Customer picker & recipient email */}
                  <div className="quotation-form-row">
                    {/* Customer Combobox */}
                    <div
                      className="quotation-field-wrap quotation-combobox-wrap"
                      ref={customerDropdownRef}
                    >
                      <label htmlFor="quotation-customer-input">
                        Customer{" "}
                        <span className="quotation-required-star">*</span>
                      </label>
                      <div
                        className={`quotation-input-box${
                          contactNameErr ? " error" : ""
                        }`}
                        data-clickable="true"
                        onClick={() =>
                          setIsCustomerDropdownOpen((prev) => !prev)
                        }
                      >
                        <LuSearch />
                        <ClearableSearchInput
                          id="quotation-customer-input"
                          role="combobox"
                          aria-expanded={isCustomerDropdownOpen}
                          aria-autocomplete="list"
                          autoComplete="off"
                          aria-required="true"
                          aria-invalid={contactNameErr}
                          aria-describedby={
                            contactNameErr
                              ? "quotation-customer-error"
                              : undefined
                          }
                          placeholder="Select or search customer"
                          value={
                            isCustomerDropdownOpen
                              ? customerSearchQuery
                              : values.name || customerSearchQuery
                          }
                          onChange={(e) => {
                            setCustomerSearchQuery(e.target.value);
                            if (e.target.value === "") {
                              setValues((previous) => ({
                                ...previous,
                                contactID: "",
                                name: "",
                                recipientEmail: "",
                              }));
                              setRecipientEmailError("");
                              setIsDirty(true);
                              setIsSaved(false);
                            }
                            if (!isCustomerDropdownOpen)
                              setIsCustomerDropdownOpen(true);
                          }}
                          onFocus={() => setIsCustomerDropdownOpen(true)}
                          onClick={(event) => {
                            event.stopPropagation();
                            setIsCustomerDropdownOpen(true);
                          }}
                        />
                        <LuChevronDown />
                      </div>
                      {contactNameErr && (
                        <span
                          id="quotation-customer-error"
                          className="quotation-field-error"
                          role="alert"
                        >
                          Please select a customer.
                        </span>
                      )}

                      {/* Dropdown Menu */}
                      {isCustomerDropdownOpen && (
                        <div className="quotation-autocomplete-dropdown">
                          <button
                            type="button"
                            className="quotation-autocomplete-add-action"
                            disabled={isCustomerSaving}
                            onClick={() => {
                              setIsCustomerDropdownOpen(false);
                              setEditingCustomer(null);
                              setCustomerFieldErrors({});
                              setCustomerModalError("");
                              setNewCustomerForm({
                                companyName: customerSearchQuery,
                                contactPerson: "",
                                mobile: "+61",
                                email: "",
                                abn: "",
                                paymentTerms: "14 Days",
                                autoReminders: true,
                              });
                              setIsCustomerModalOpen(true);
                            }}
                          >
                            <LuPlus />
                            <span>Add New Customer</span>
                          </button>
                          {filteredCustomers.length > 0 ? (
                            filteredCustomers.map((contact) => (
                              <div
                                key={contact._id}
                                className="quotation-autocomplete-item quotation-customer-option"
                              >
                                <button
                                  type="button"
                                  className="quotation-customer-option-select"
                                  onClick={() => handleSelectCustomer(contact)}
                                >
                                  <strong>
                                    {contact.companyName || contact.name}
                                  </strong>
                                  <span>
                                    {contact.emailAddress ||
                                      contact.customerEmailAddress ||
                                      contact.email ||
                                      "No email"}
                                  </span>
                                </button>
                                {canManageCustomers && (
                                  <div className="quotation-customer-option-actions">
                                    <button
                                      type="button"
                                      aria-label={`Edit ${contact.companyName || contact.name}`}
                                      title="Edit customer"
                                      disabled={isCustomerSaving}
                                      onClick={() => handleEditCustomer(contact)}
                                    >
                                      <LuPencil aria-hidden="true" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="quotation-autocomplete-no-match">
                              <strong>No customers found</strong>
                              <span>
                                Try a different name or add a new customer.
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Recipient Email */}
                    <div className="quotation-field-wrap">
                      <label htmlFor="quotation-recipient-email">
                        Recipient email
                      </label>
                      <div
                        className={`quotation-input-box quotation-recipient-email-box${
                          visibleRecipientEmailError ? " error" : ""
                        }`}
                      >
                        <input
                          id="quotation-recipient-email"
                          type="email"
                          aria-invalid={Boolean(visibleRecipientEmailError)}
                          aria-describedby={
                            visibleRecipientEmailError
                              ? "quotation-recipient-email-error"
                              : undefined
                          }
                          placeholder="accounts@example.com"
                          value={values.recipientEmail || ""}
                          onChange={(e) => {
                            setIsDirty(true);
                            setIsSaved(false);
                            if (recipientEmailError)
                              setRecipientEmailError(
                                getRecipientEmailError(e.target.value, true)
                              );
                            setValues((prev) => ({
                              ...prev,
                              recipientEmail: e.target.value,
                            }));
                          }}
                          onBlur={(event) => {
                            const email = event.target.value.trim();
                            setRecipientEmailError(
                              getRecipientEmailError(
                                email,
                                Boolean(values.contactID) &&
                                  draftValidationField ===
                                    "quotation-recipient-email"
                              )
                            );
                            setValues((prev) => ({
                              ...prev,
                              recipientEmail: email,
                            }));
                          }}
                        />
                      </div>
                      {visibleRecipientEmailError && (
                        <span
                          id="quotation-recipient-email-error"
                          className="quotation-field-error"
                          role="alert"
                        >
                          {visibleRecipientEmailError}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Job / Project Reference */}
                  <div className="quotation-form-row full">
                    <div className="quotation-field-wrap">
                      <label>Job / project reference</label>
                      <div className="quotation-input-box">
                        <input
                          placeholder="Add a reference e.g. PO-8492"
                          value={values.purchaseOrderNumber || ""}
                          onChange={(e) => {
                            setIsDirty(true);
                            setIsSaved(false);
                            setValues((prev) => ({
                              ...prev,
                              purchaseOrderNumber: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Quote Number, Quote Date, Validity */}
                  <div className="quotation-form-row three-col">
                    {/* Quote Number */}
                    <div className="quotation-field-wrap">
                      <label htmlFor="quotation-number-input">
                        Quote number
                      </label>
                      <div className="quotation-input-box">
                        <input
                          id="quotation-number-input"
                          aria-invalid={Boolean(numberValidation.error)}
                          aria-describedby={numberValidation.error ? "quotation-number-error" : undefined}
                          value={values.quotationNumber || ""}
                          placeholder={
                            isQuoteNumberLoading
                              ? "Loading quote number..."
                              : "Quote number"
                          }
                          onChange={(event) => {
                            const quotationNumber = event.target.value;
                            quoteNumberEditedRef.current = true;
                            numberValidation.clearError();
                            setIsDirty(true);
                            setIsSaved(false);
                            setValues((previous) => ({
                              ...previous,
                              quotationNumber,
                            }));
                          }}
                        />
                      </div>
                      {numberValidation.error && (
                        <span id="quotation-number-error" className="quotation-field-error" role="alert">
                          {numberValidation.error}
                        </span>
                      )}
                    </div>

                    {/* Quote Date */}
                    <div className="quotation-field-wrap quotation-quote-date-field">
                      <label
                        id="quotation-date-label"
                        htmlFor="quotation-date-input"
                      >
                        Quote date
                      </label>
                      <PickDate
                        ariaLabelledBy="quotation-date-label"
                        id="quotation-date-input"
                        error={Boolean(
                          draftValidationField === "quotation-date-input" &&
                            draftDateError
                        )}
                        helperText={
                          draftValidationField === "quotation-date-input"
                            ? draftDateError
                            : ""
                        }
                        toggleCalendarOnIconClick
                        selected={
                          values.quotationDate
                            ? new Date(values.quotationDate)
                            : null
                        }
                        onChange={(date) => handleDateChange(date)}
                        placeholder="Select quote date"
                      />
                    </div>

                    {/* Validity */}
                    <div className="quotation-field-wrap">
                      <label>Validity</label>
                      <CatalogueDropdown
                        ariaLabel="Validity"
                        value={values.validUpto || "14 Days"}
                        options={paymentTermsOptions.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        onChange={handleValidityChange}
                      />
                    </div>
                  </div>

                  <p className="quotation-field-footnote">
                    Starts when the quote is sent. Drafts have no active expiry.
                  </p>
                </div>
              </section>

              {/* CARD 2: ITEMS & PRICING */}
              <section className="quotation-editor-card">
                <div className="quotation-card-header">
                  <h3>Items &amp; pricing</h3>
                  <p>
                    {values.gstType === "No GST"
                      ? "No GST applies to this quotation."
                      : values.gstType === "Inclusive"
                      ? "Unit prices and line totals include GST."
                      : "GST is calculated and added to the quote total."}
                  </p>
                </div>

                <div className="quotation-card-body">
                  {/* Pricing basis row (Figma Node 610:77741 - No container background/border) */}
                  <div className="quotation-pricing-basis-row">
                    <span className="quotation-pricing-basis-label">
                      Pricing basis
                    </span>
                    <div className="quotation-pricing-basis-select-wrap quotation-custom-dropdown-wrap">
                      <CatalogueDropdown
                        ariaLabel="Pricing basis"
                        value={values.gstType || "Inclusive"}
                        options={[
                          { value: "Inclusive", label: "GST inclusive" },
                          { value: "Exclusive", label: "GST exclusive" },
                          { value: "No GST", label: "No GST" },
                        ]}
                        onChange={(gstType) => {
                          setIsDirty(true);
                          setIsSaved(false);
                          setValues((prev) => ({ ...prev, gstType }));
                        }}
                      />
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="quotation-items-table-wrapper document-items-aligned">
                    <div className="quotation-items-table-header">
                      <div className="quotation-col-item">Item</div>
                      <div className="quotation-col-qty">Qty</div>
                      <div className="quotation-col-price">Unit price</div>
                      <div className="quotation-col-gst">GST</div>
                      <div className="quotation-col-total">Line total</div>
                      <div className="quotation-col-action" />
                    </div>

                    <div
                      ref={itemsTableBodyRef}
                      className={`quotation-items-table-body${
                        itemRowValues.length > MAX_VISIBLE_ITEM_ROWS
                          ? " is-scrollable"
                          : ""
                      }${draggedItemIndex !== null ? " is-reordering" : ""}`}
                      style={
                        itemsViewportHeight
                          ? { maxHeight: `${itemsViewportHeight}px` }
                          : undefined
                      }
                      tabIndex={
                        itemRowValues.length > MAX_VISIBLE_ITEM_ROWS
                          ? 0
                          : undefined
                      }
                      aria-label="Quotation items"
                    >
                      {itemRowValues.map((row, idx) => (
                        <div
                          key={idx}
                          data-quotation-item-index={idx}
                          style={getItemRowDragStyle(idx)}
                          className={`quotation-item-row-card document-items-aligned${
                            draggedItemIndex === idx ? " is-dragging" : ""
                          }${
                            dropTargetIndex === idx && draggedItemIndex !== idx
                              ? draggedItemIndex > idx
                                ? " drop-before"
                                : " drop-after"
                              : ""
                          }`}
                        >
                          {/* Top Row: Item search combobox */}
                          <div
                            className="quotation-item-search-row"
                            ref={(el) => (itemDropdownRefs.current[idx] = el)}
                          >
                            <div className="quotation-item-search-box">
                              <button
                                id={`quotation-item-move-${idx}`}
                                type="button"
                                className="quotation-item-drag-handle"
                                aria-label={`Move ${
                                  row.itemName || `line ${idx + 1}`
                                }. Use Up or Down arrow keys to reorder.`}
                                title="Drag to reorder, or use Up and Down arrow keys"
                                disabled={
                                  itemRowValues.length < 2 || isSavingQuote
                                }
                                aria-pressed={draggedItemIndex === idx}
                                onPointerDown={(event) =>
                                  handleItemDragStart(event, idx)
                                }
                                onPointerMove={handleItemDragMove}
                                onPointerUp={handleItemDragEnd}
                                onPointerCancel={resetItemDrag}
                                onLostPointerCapture={handleItemDragLostCapture}
                                onKeyDown={(event) => {
                                  if (
                                    event.key !== "ArrowUp" &&
                                    event.key !== "ArrowDown"
                                  )
                                    return;
                                  event.preventDefault();
                                  handleMoveLineRow(
                                    idx,
                                    idx + (event.key === "ArrowUp" ? -1 : 1)
                                  );
                                }}
                              >
                                <LuGripVertical />
                              </button>
                              <LuSearch />
                              <ClearableSearchInput
                                id={`quotation-item-search-${idx}`}
                                role="combobox"
                                aria-label={`Item name for item ${idx + 1}`}
                                aria-invalid={Boolean(
                                  draftValidationAttempted &&
                                    draftItemErrors[idx]?.itemName
                                )}
                                aria-describedby={
                                  draftValidationAttempted &&
                                  draftItemErrors[idx]?.itemName
                                    ? `quotation-itemName-error-${idx}`
                                    : undefined
                                }
                                aria-autocomplete="list"
                                aria-expanded={activeItemDropdownIdx === idx}
                                aria-controls={`quotation-item-results-${idx}`}
                                placeholder="Search catalogue item e.g. Safety upgrade"
                                value={
                                  activeItemDropdownIdx === idx
                                    ? itemSearchQueries[idx] ??
                                      row.itemName ??
                                      ""
                                    : row.itemName || ""
                                }
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setItemSearchQueries((prev) => ({
                                    ...prev,
                                    [idx]: val,
                                  }));
                                  handleItemRowChange(idx, "itemName", val);
                                  if (activeItemDropdownIdx !== idx)
                                    setActiveItemDropdownIdx(idx);
                                }}
                                onFocus={() => {
                                  setActiveItemDropdownIdx(idx);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Escape")
                                    setActiveItemDropdownIdx(null);
                                }}
                              />
                              <button
                                type="button"
                                className="quotation-item-search-toggle"
                                aria-haspopup="listbox"
                                aria-expanded={activeItemDropdownIdx === idx}
                                aria-label={`${
                                  activeItemDropdownIdx === idx
                                    ? "Close"
                                    : "Open"
                                } catalogue item options`}
                                onClick={() =>
                                  setActiveItemDropdownIdx(
                                    activeItemDropdownIdx === idx ? null : idx
                                  )
                                }
                              >
                                <LuChevronDown />
                              </button>
                            </div>

                            {draftValidationAttempted &&
                              draftItemErrors[idx]?.itemName && (
                                <span
                                  id={`quotation-itemName-error-${idx}`}
                                  className="quotation-field-error"
                                  role="alert"
                                >
                                  {draftItemErrors[idx].itemName}
                                </span>
                              )}

                            {/* Catalogue Item Dropdown (Matching Figma Node 722:28011) */}
                            {activeItemDropdownIdx === idx && (
                              <CatalogueItemDropdown
                                anchorEl={itemDropdownRefs.current[idx]}
                                onClose={() => setActiveItemDropdownIdx(null)}
                              >
                                <div
                                  id={`quotation-item-results-${idx}`}
                                  className="quotation-item-search-results-list"
                                  role="listbox"
                                  aria-label="Catalogue items"
                                >
                                  <button
                                    type="button"
                                    role="option"
                                    aria-selected={false}
                                    className="quotation-item-result-card quotation-new-item-option"
                                    onClick={() =>
                                      handleOpenNewItem(
                                        idx,
                                        itemSearchQueries[idx] ||
                                          row.itemName ||
                                          ""
                                      )
                                    }
                                  >
                                    <div className="quotation-item-result-top">
                                      <div className="quotation-item-result-name-group">
                                        <LuPlus className="quotation-match-icon" />
                                        <span className="quotation-item-result-name">
                                          Add new item
                                        </span>
                                      </div>
                                    </div>
                                    <p className="quotation-item-result-sub">
                                      Add this item to your catalogue
                                    </p>
                                  </button>
                                  {getMatchingCatalogueItems(
                                    itemSearchQueries[idx] ?? row.itemName ?? ""
                                  ).map((it) => {
                                    const isSelected =
                                      row.itemID === it._id ||
                                      (Boolean(row.itemName) &&
                                        row.itemName === it.itemName);
                                    return (
                                      <div
                                        key={it._id}
                                        className="quotation-catalogue-option-row"
                                      >
                                        <button
                                          type="button"
                                          role="option"
                                          aria-selected={isSelected}
                                          className="quotation-autocomplete-item quotation-catalogue-option"
                                          onClick={() =>
                                            handleSelectCatalogueItem(idx, it)
                                          }
                                        >
                                          <strong>
                                            {it.itemName || it.itemDescription}
                                          </strong>
                                          <span>
                                            {[
                                              it.itemCode,
                                              it.itemDescription || it.itemName,
                                              `GST $${formatCurrencyAUD(
                                                parseFloat(it.itemPrice || 0)
                                              )}`,
                                              it.discountValue
                                                ? `${it.discountValue}% off`
                                                : null,
                                            ]
                                              .filter(Boolean)
                                              .join(" · ")}
                                          </span>
                                        </button>
                                        <button
                                          type="button"
                                          className="quotation-catalogue-option-edit"
                                          aria-label={`Edit ${
                                            it.itemName || "catalogue item"
                                          }`}
                                          title="Edit item"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setActiveItemDropdownIdx(null);
                                            setEditingCatalogueItem({
                                              item: it,
                                              rowIndex: idx,
                                            });
                                          }}
                                        >
                                          <LuPencil aria-hidden="true" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </CatalogueItemDropdown>
                            )}
                          </div>

                          {/* Middle Row: Description, Qty, Unit Price, GST, Line Total, Trash/Remove */}
                          <div className="quotation-item-details-row">
                            <div
                              className="quotation-col-desc"
                              data-label="Description"
                            >
                              <input
                                placeholder="Add a description"
                                value={row.itemDescription || ""}
                                onChange={(e) =>
                                  handleItemRowChange(
                                    idx,
                                    "itemDescription",
                                    e.target.value
                                  )
                                }
                              />
                            </div>

                            <div
                              className="quotation-col-qty-input"
                              data-label="Quantity"
                            >
                              <input
                                id={`quotation-itemQuantity-${idx}`}
                                aria-label={`Quantity for item ${idx + 1}`}
                                aria-invalid={Boolean(
                                  draftValidationAttempted &&
                                    draftItemErrors[idx]?.itemQuantity
                                )}
                                aria-describedby={
                                  draftValidationAttempted &&
                                  draftItemErrors[idx]?.itemQuantity
                                    ? `quotation-itemQuantity-error-${idx}`
                                    : undefined
                                }
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={4}
                                value={row.itemQuantity ?? ""}
                                onChange={(e) =>
                                  handleItemRowChange(
                                    idx,
                                    "itemQuantity",
                                    e.target.value
                                  )
                                }
                              />
                              {draftValidationAttempted &&
                                draftItemErrors[idx]?.itemQuantity && (
                                  <span
                                    id={`quotation-itemQuantity-error-${idx}`}
                                    className="quotation-field-error"
                                    role="alert"
                                  >
                                    {draftItemErrors[idx].itemQuantity}
                                  </span>
                                )}
                            </div>

                            <div
                              className="quotation-col-price-input"
                              data-label="Unit price"
                            >
                              <input
                                id={`quotation-itemPrice-${idx}`}
                                aria-label={`Unit price for item ${idx + 1}`}
                                aria-invalid={Boolean(
                                  draftValidationAttempted &&
                                    draftItemErrors[idx]?.itemPrice
                                )}
                                aria-describedby={
                                  draftValidationAttempted &&
                                  draftItemErrors[idx]?.itemPrice
                                    ? `quotation-itemPrice-error-${idx}`
                                    : undefined
                                }
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={row.itemPrice || ""}
                                onChange={(e) =>
                                  handleItemRowChange(
                                    idx,
                                    "itemPrice",
                                    e.target.value
                                  )
                                }
                              />
                              {draftValidationAttempted &&
                                draftItemErrors[idx]?.itemPrice && (
                                  <span
                                    id={`quotation-itemPrice-error-${idx}`}
                                    className="quotation-field-error"
                                    role="alert"
                                  >
                                    {draftItemErrors[idx].itemPrice}
                                  </span>
                                )}
                            </div>

                            <div
                              className="quotation-col-gst-select quotation-custom-dropdown-wrap"
                              data-label="GST"
                            >
                              <CatalogueDropdown
                                ariaLabel={`GST for ${row.itemName || "item"}`}
                                disabled={values.gstType === "No GST"}
                                value={
                                  values.gstType === "No GST"
                                    ? 0
                                    : row.gst !== undefined
                                    ? row.gst
                                    : 10
                                }
                                options={[
                                  { value: 10, label: "10%" },
                                  { value: 0, label: "0%" },
                                ]}
                                onChange={(gst) =>
                                  handleItemRowChange(idx, "gst", gst)
                                }
                              />
                            </div>

                            <div
                              className="quotation-col-line-total"
                              data-label="Line total"
                            >
                              <span>
                                $
                                {formatCurrencyAUD(
                                  parseFloat(row.totalItemPrice) || 0
                                )}
                              </span>
                            </div>

                            <div className="quotation-col-row-action">
                              {(idx > 0 ||
                                row.itemID ||
                                row.itemName?.trim() ||
                                row.itemDescription?.trim()) && (
                                <button
                                  type="button"
                                  className="quotation-item-remove-btn"
                                  onClick={() => handleRemoveLineRow(idx)}
                                  aria-label={`Remove ${
                                    row.itemName ||
                                    row.itemDescription ||
                                    `line ${idx + 1}`
                                  }`}
                                  title="Remove line"
                                >
                                  <LuTrash2 />
                                  <span>Remove line</span>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Bottom Row: Line Item Discount */}
                          <div className="quotation-item-discount-row">
                            <div className="quotation-discount-type-wrap">
                              <label>Discount type</label>
                              <CatalogueDropdown
                                portalMenu
                                ariaLabel={`Discount type for ${
                                  row.itemName || "item"
                                }`}
                                value={row.discountType || "None"}
                                options={[
                                  { value: "None", label: "None" },
                                  { value: "Percentage", label: "Percentage" },
                                  { value: "Fixed", label: "Fixed amount" },
                                ]}
                                onChange={(discountType) =>
                                  handleItemRowChange(
                                    idx,
                                    "discountType",
                                    discountType
                                  )
                                }
                              />
                            </div>

                            <div
                              className={`quotation-discount-val-wrap${
                                row.discountType === "None" ? " disabled" : ""
                              }`}
                            >
                              <label>Discount value</label>
                              <div className="quotation-input-box">
                                <input
                                  type="number"
                                  min="0"
                                  step={
                                    row.discountType === "Percentage"
                                      ? "1"
                                      : "0.01"
                                  }
                                  disabled={row.discountType === "None"}
                                  placeholder={
                                    row.discountType === "Percentage"
                                      ? "%"
                                      : "$0.00"
                                  }
                                  value={
                                    row.discountType === "None"
                                      ? "0.00"
                                      : row.discountValue || ""
                                  }
                                  onChange={(e) =>
                                    handleItemRowChange(
                                      idx,
                                      "discountValue",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="quotation-account-wrap">
                              <label>Account</label>
                              <CatalogueDropdown
                                portalMenu
                                ariaLabel={`Account for ${
                                  row.itemName || "item"
                                }`}
                                value={row.categoryCodeName || ""}
                                options={[
                                  { value: "", label: "None" },
                                  ...catalogueAccountOptions,
                                ]}
                                onChange={(account) =>
                                  handleItemRowChange(
                                    idx,
                                    "categoryCodeName",
                                    account
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Items Action Bar: Add Line, New Catalogue Item, Total Discounts */}
                  <div className="quotation-items-actions-bar">
                    <button
                      type="button"
                      className="quotation-action-outline-btn"
                      onClick={handleAddLineRow}
                    >
                      <LuPlus />
                      <span>Add line</span>
                    </button>
                    <button
                      type="button"
                      className="quotation-action-link-btn"
                      onClick={() => handleOpenNewItem()}
                    >
                      <LuPlus />
                      <span>New catalogue item</span>
                    </button>

                    <div className="quotation-items-spacer" />
                    <div className="quotation-total-discounts" aria-live="polite">
                      <span>Total item discounts</span>
                      <strong>
                        ${formatCurrencyAUD(values.totalDiscountAmount || 0)}
                      </strong>
                    </div>
                  </div>

                  <p className="quotation-field-footnote">
                    {values.gstType === "No GST"
                      ? "No GST applies to this quotation."
                      : values.gstType === "Inclusive"
                      ? "GST is included in these prices, not added again to the quote total."
                      : "GST is calculated from each line and added to the quote total."}
                  </p>
                </div>
              </section>

              {/* CARD 3: NOTES & TERMS */}
              <section className="quotation-editor-card">
                <div className="quotation-card-header">
                  <h3>Notes &amp; terms</h3>
                  <p>These details appear on the customer&apos;s quote.</p>
                </div>

                <div className="quotation-card-body">
                  <div className="quotation-form-row">
                    <div className="quotation-field-wrap">
                      <label>Scope / customer notes</label>
                      <textarea
                        className="quotation-textarea-box"
                        rows={4}
                        placeholder="Supply and install the items listed above..."
                        value={values.paymentInstruction || ""}
                        onChange={(e) => {
                          setIsDirty(true);
                          setIsSaved(false);
                          setValues((prev) => ({
                            ...prev,
                            paymentInstruction: e.target.value,
                          }));
                        }}
                      />
                    </div>

                    <div className="quotation-field-wrap">
                      <label htmlFor="quotation-terms">
                        Terms (auto-generated)
                      </label>
                      <textarea
                        id="quotation-terms"
                        className="quotation-textarea-box"
                        rows={4}
                        value={values.terms || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* CARD 4: ATTACHMENTS */}
              <section className="quotation-editor-card quotation-attachments-card">
                <div className="quotation-card-header">
                  <h3>Attachments</h3>
                </div>
                <div className="quotation-card-body">
                  <div className="quotation-attachment-summary">
                    <strong>
                      {quoDocuments.length
                        ? quoDocuments.length + " attachment(s)"
                        : "No attachments added"}
                    </strong>
                    <p>Optional supporting files. The quote PDF is separate.</p>
                  </div>
                  {quoDocuments.length > 0 && (
                    <div className="quotation-attachments-list">
                      {quoDocuments.map((doc, idx) => (
                        <div
                          key={doc._id || idx}
                          className="quotation-attachment-item"
                        >
                          <div
                            className="quotation-attachment-info"
                            data-clickable="true"
                            onClick={() => handleViewDocument(doc)}
                          >
                            <LuFileText aria-hidden="true" />
                            <span>
                              {doc.documentName || doc.documentFileName}
                              {doc.file && " (pending save)"}
                            </span>
                          </div>
                          <div className="quotation-attachment-actions">
                            <button
                              type="button"
                              className="quotation-attachment-view-btn"
                              onClick={() => handleViewDocument(doc)}
                              aria-label={"View " + doc.documentName}
                              title="View document"
                            >
                              <LuEye aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="quotation-attachment-remove-btn"
                              onClick={() => handleDeleteDocument(doc)}
                              aria-label={"Remove " + doc.documentName}
                              title="Delete document"
                            >
                              <LuTrash2 aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="quotation-attachment-upload">
                    <LuUpload aria-hidden="true" />
                    Add attachment
                    <input type="file" onChange={handleFileUpload} />
                  </label>
                  {fileError && (
                    <span
                      className="quotation-field-error quotation-attachment-error"
                      role="alert"
                    >
                      {fileError}
                    </span>
                  )}
                </div>
              </section>
            </div>

            {/* RIGHT COLUMN: REVIEW & TOTALS SIDEBAR */}
            <aside className="quotation-totals-sidebar">
              {/* GRAND TOTAL CARD */}
              <div className="quotation-grand-total-card">
                <div className="quotation-grand-total-header">
                  <span>Total including GST</span>
                  <LuFileText />
                </div>
                <div className="quotation-grand-total-headline">
                  <strong>
                    ${formatCurrencyAUD(parseFloat(values.finalAmount) || 0)}
                  </strong>
                  <span>
                    {displayCurrencyCode} · {filledItemCount} item
                    {filledItemCount === 1 ? "" : "s"} ·{" "}
                    {values.emailSent > 0 ? "Sent" : "Not sent"}
                  </span>
                </div>
              </div>

              {/* DOCUMENT ACTIONS */}
              <div className="quotation-sidebar-doc-actions">
                <button
                  type="button"
                  className="quotation-sidebar-save-btn"
                  disabled={isSavingQuote || sendingEmail}
                  aria-label="Save draft from summary"
                  aria-busy={isSavingQuote || undefined}
                  onClick={() => handleSaveQuotation()}
                >
                  {isSavingQuote ? (
                    <CircularProgress
                      className="quotation-save-draft-spinner"
                      color="inherit"
                      size={16}
                      thickness={5}
                      aria-hidden="true"
                    />
                  ) : (
                    <LuSave aria-hidden="true" />
                  )}
                  <span>{isSavingQuote ? "Saving..." : "Save draft"}</span>
                </button>
                <button
                  type="button"
                  className="quotation-sidebar-preview-btn"
                  disabled={!canUseDocumentActions}
                  onClick={handlePreviewQuote}
                >
                  <LuEye />
                  <span>Preview quote</span>
                </button>
                <button
                  type="button"
                  className="quotation-sidebar-download-btn"
                  disabled={!canUseDocumentActions}
                  onClick={handleDownloadPdf}
                >
                  <LuDownload />
                  <span>Download PDF</span>
                </button>
                {!canUseDocumentActions && (
                  <p className="quotation-sidebar-doc-hint" role="status">
                    <LuInfo aria-hidden="true" />
                    <span>
                      {isSavingQuote
                        ? "Saving draft. Preview and Download PDF will be available when saving finishes."
                        : `Save ${
                            hasSavedQuotation ? "your changes" : "the draft"
                          } to preview the quotation and download its PDF.`}
                    </span>
                  </p>
                )}
              </div>

              {/* QUOTE SUMMARY CARD */}
              <div className="quotation-summary-card">
                <h4>Quote summary</h4>
                <div className="quotation-summary-breakdown-row">
                  <span className="quotation-summary-label">
                    Subtotal excluding GST (after item discounts)
                  </span>
                  <span className="quotation-summary-value">
                    ${formatCurrencyAUD(parseFloat(values.totalAmount) || 0)}
                  </span>
                </div>

                <div className="quotation-summary-breakdown-row">
                  <span className="quotation-summary-label">
                    {values.gstType === "No GST"
                      ? "Total item discounts"
                      : "Total item discounts (GST inclusive)"}
                  </span>
                  <span className="quotation-summary-value">
                    $
                    {formatCurrencyAUD(
                      parseFloat(values.totalDiscountAmount) || 0
                    )}
                  </span>
                </div>

                <div className="quotation-summary-breakdown-row">
                  <span className="quotation-summary-label">
                    {values.gstType === "No GST"
                      ? "No GST"
                      : `GST 10% (${
                          values.gstType === "Inclusive" ? "included" : "added"
                        })`}
                  </span>
                  <span className="quotation-summary-value">
                    ${formatCurrencyAUD(parseFloat(values.totalGSTAmount) || 0)}
                  </span>
                </div>

                <hr className="quotation-summary-divider" />

                <div className="quotation-summary-breakdown-row total">
                  <span className="quotation-summary-label">
                    {values.gstType === "No GST"
                      ? "Total"
                      : "Total including GST"}
                  </span>
                  <span className="quotation-summary-value">
                    ${formatCurrencyAUD(parseFloat(values.finalAmount) || 0)}
                  </span>
                </div>

                <p className="quotation-summary-footnote">
                  {values.gstType === "No GST"
                    ? "No GST applies. Discounts are already applied to the total."
                    : "Subtotal excluding GST is after item discounts. Total item discounts include GST and are shown as an informational breakdown only."}
                </p>
              </div>

              {/* DRAFT DELIVERY GUIDANCE */}
              <div className="quotation-guidance-card">
                <div className="quotation-guidance-header">
                  <LuSend />
                  <span>Not sent to customer</span>
                </div>
                <p>
                  Save draft keeps your edits without sending anything to{" "}
                  {values.name || "the customer"}.
                </p>
                <p>
                  Send quote opens a review of the recipient and email before
                  you confirm sending.
                </p>
                <p>
                  Validity starts on sending, not while this quote is a draft.
                </p>

                <button
                  type="button"
                  className="quotation-panel-send-btn"
                  disabled={isSavingQuote || sendingEmail}
                  onClick={handleOpenSendModal}
                >
                  <LuSend />
                  <span>Send quote</span>
                </button>
              </div>
            </aside>
          </div>

          {Boolean(QuotationID || values.quotationID) && (
            <section
              className="quote-details-history-section quotation-editor-history-section"
              aria-label="History and notes"
            >
              <div className="quote-history-header">
              <div>
                  <h3 className="quote-history-title">History &amp; notes</h3>
                  <p className="quote-history-subtitle">
                    All activity {"\u00b7"} Newest first {"\u00b7"} Audit events cannot be edited.
                </p>
              </div>
              <button
                type="button"
                  className="quotation-history-link-btn"
                  onClick={handleViewFullHistory}
              >
                  View full history
              </button>
              </div>

              <div className="quote-history-privacy-banner">
              <LuLock />
              <div>
                <strong>Only visible to your team</strong>
                <p>
                  History and internal notes never appear on the customer
                  document or PDF.
                </p>
              </div>
            </div>

              <div className="quote-history-two-col">
                <div className="quote-history-activity-col">
                {historyRows.length ? (
                  historyRows.slice(0, 3).map((row, index) => {
                  const isNote = /note/i.test(`${row.action || ""}`);
                  return (
                    <article
                      className={`quotation-history-event${
                        isNote ? " note" : ""
                      }`}
                      key={row._id || `${row.dateAndTime}-${index}`}
                    >
                      <strong>
                        {isNote
                          ? "Internal note"
                          : row.action || "Updated quote"}
                      </strong>
                      <span>
                        {row.name || values.createdByName || userName}
                      </span>
                      <time>
                        {formatActivityTimestamp(
                          row.dateAndTime,
                          formattedCreationDate
                        )}
                      </time>
                      <p>{row.details || "Quotation updated."}</p>
                    </article>
                  );
                })
              ) : (
                <div className="quotation-history-empty">
                    No saved activity yet.
                </div>
              )}
                </div>

                <div className="quote-history-composer-col">
                  <div className="quotation-history-composer">
              <label htmlFor="quotation-internal-note">
                Add an internal note
              </label>
              <textarea
                id="quotation-internal-note"
                value={internalNote}
                onChange={(event) => setInternalNote(event.target.value)}
                placeholder="Write a note for your team…"
              />
              <span>Only visible to your team</span>
              <button
                type="button"
                disabled={!internalNote.trim()}
                onClick={handleAddInternalNote}
              >
                <LuPlus />
                Add note
              </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
      </div>

      {/* =========================================================================
          MODALS & DIALOGS
          ========================================================================= */}

      {/* QUICK-CREATE CUSTOMER MODAL (Figma Node 835:55403) */}
      {isCustomerModalOpen && (
        <div
          className="quotation-dialog-scrim"
          data-clickable={!isCustomerSaving}
          onClick={customerModalChanges.requestClose}
        >
          <div
            className="quotation-modal-dialog quotation-customer-modal"
            role="dialog"
            aria-modal="true"
            aria-label={editingCustomer ? "Edit customer" : "New customer"}
            data-clickable={false}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quotation-modal-header">
              <h3>{editingCustomer ? "Edit customer" : "New customer"}</h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                disabled={isCustomerSaving}
                onClick={customerModalChanges.requestClose}
              >
                <LuX />
              </button>
            </div>

            <div className="quotation-modal-body">
              <p className="quotation-modal-caption">
                {editingCustomer
                  ? "Update customer details"
                  : "Quick create · Contact type: Customer · Status: Active"}
              </p>

              {customerModalError && (
                <div className="quotation-modal-alert" role="alert">
                  <LuCircleAlert />
                  <span>{customerModalError}</span>
                </div>
              )}

              <div className="quotation-field-wrap">
                <label htmlFor="new-customer-company">
                  Company name{" "}
                  <span className="quotation-required-star">*</span>
                </label>
                <div
                  className={`quotation-input-box${
                    customerFieldErrors.companyName ? " error" : ""
                  }`}
                >
                  <input
                    id="new-customer-company"
                    maxLength={200}
                    autoComplete="off"
                    disabled={isCustomerSaving}
                    aria-invalid={Boolean(customerFieldErrors.companyName)}
                    aria-describedby={
                      customerFieldErrors.companyName
                        ? "new-customer-company-error"
                        : undefined
                    }
                    placeholder="Company or business name"
                    value={newCustomerForm.companyName}
                    onChange={(e) =>
                      handleCustomerFieldChange("companyName", e.target.value)
                    }
                    onBlur={(e) =>
                      handleCustomerFieldBlur("companyName", e.target.value)
                    }
                    autoFocus
                  />
                </div>
                {customerFieldErrors.companyName && (
                  <span
                    id="new-customer-company-error"
                    className="quotation-customer-field-error"
                    role="alert"
                  >
                    {customerFieldErrors.companyName}
                  </span>
                )}
              </div>

              <div className="quotation-modal-form-row">
                <div className="quotation-field-wrap">
                  <label htmlFor="new-customer-person">Contact person</label>
                  <div
                    className={`quotation-input-box${
                      customerFieldErrors.contactPerson ? " error" : ""
                    }`}
                  >
                    <input
                      id="new-customer-person"
                      maxLength={100}
                      autoComplete="off"
                      disabled={isCustomerSaving}
                      aria-invalid={Boolean(customerFieldErrors.contactPerson)}
                      aria-describedby={
                        customerFieldErrors.contactPerson
                          ? "new-customer-person-error"
                          : undefined
                      }
                      placeholder="e.g. Alex Morgan"
                      value={newCustomerForm.contactPerson}
                      onChange={(e) =>
                        handleCustomerFieldChange(
                          "contactPerson",
                          e.target.value
                        )
                      }
                      onBlur={(e) =>
                        handleCustomerFieldBlur("contactPerson", e.target.value)
                      }
                    />
                  </div>
                  {customerFieldErrors.contactPerson && (
                    <span
                      id="new-customer-person-error"
                      className="quotation-customer-field-error"
                      role="alert"
                    >
                      {customerFieldErrors.contactPerson}
                    </span>
                  )}
                </div>

                <div className="quotation-field-wrap">
                  <label htmlFor="new-customer-mobile">Mobile</label>
                  <div
                    className={`quotation-input-box${
                      customerFieldErrors.mobile ? " error" : ""
                    }`}
                  >
                    <input
                      id="new-customer-mobile"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="off"
                      disabled={isCustomerSaving}
                      aria-invalid={Boolean(customerFieldErrors.mobile)}
                      aria-describedby={
                        customerFieldErrors.mobile
                          ? "new-customer-mobile-error"
                          : undefined
                      }
                      placeholder="+61 412 345 678"
                      value={newCustomerForm.mobile}
                      onChange={(e) =>
                        handleCustomerFieldChange("mobile", e.target.value)
                      }
                      onBlur={(e) =>
                        handleCustomerFieldBlur("mobile", e.target.value)
                      }
                    />
                  </div>
                  {customerFieldErrors.mobile && (
                    <span
                      id="new-customer-mobile-error"
                      className="quotation-customer-field-error"
                      role="alert"
                    >
                      {customerFieldErrors.mobile}
                    </span>
                  )}
                </div>
              </div>

              <div className="quotation-field-wrap">
                <label htmlFor="new-customer-email">
                  Email <span className="quotation-required-star">*</span>
                </label>
                <div
                  className={`quotation-input-box${
                    customerFieldErrors.email ? " error" : ""
                  }`}
                >
                  <input
                    id="new-customer-email"
                    type="email"
                    maxLength={254}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    disabled={isCustomerSaving}
                    aria-invalid={Boolean(customerFieldErrors.email)}
                    aria-describedby={
                      customerFieldErrors.email
                        ? "new-customer-email-error"
                        : undefined
                    }
                    placeholder="accounts@company.com.au"
                    value={newCustomerForm.email}
                    onChange={(e) =>
                      handleCustomerFieldChange("email", e.target.value)
                    }
                    onBlur={(e) =>
                      handleCustomerFieldBlur("email", e.target.value)
                    }
                  />
                </div>
                {customerFieldErrors.email && (
                  <span
                    id="new-customer-email-error"
                    className="quotation-customer-field-error"
                    role="alert"
                  >
                    {customerFieldErrors.email}
                  </span>
                )}
              </div>

              <div className="quotation-modal-form-row">
                <div className="quotation-field-wrap">
                  <label htmlFor="new-customer-abn">ABN (optional)</label>
                  <div
                    className={`quotation-input-box${
                      customerFieldErrors.abn ? " error" : ""
                    }`}
                  >
                    <input
                      id="new-customer-abn"
                      autoComplete="off"
                      inputMode="numeric"
                      maxLength={14}
                      disabled={isCustomerSaving}
                      aria-invalid={Boolean(customerFieldErrors.abn)}
                      aria-describedby={
                        customerFieldErrors.abn
                          ? "new-customer-abn-error"
                          : undefined
                      }
                      placeholder="11 digit ABN"
                      value={newCustomerForm.abn}
                      onChange={(e) =>
                        handleCustomerFieldChange("abn", e.target.value)
                      }
                      onBlur={(e) =>
                        handleCustomerFieldBlur("abn", e.target.value)
                      }
                    />
                  </div>
                  {customerFieldErrors.abn && (
                    <span
                      id="new-customer-abn-error"
                      className="quotation-customer-field-error"
                      role="alert"
                    >
                      {customerFieldErrors.abn}
                    </span>
                  )}
                </div>

                <div className="quotation-field-wrap">
                  <label>Payment terms</label>
                  <CatalogueDropdown
                    ariaLabel="Payment terms"
                    value={newCustomerForm.paymentTerms}
                    options={paymentTermsOptions.map((option) => ({
                      value: option,
                      label: option,
                    }))}
                    onChange={(paymentTerms) => {
                      if (!isCustomerSaving)
                        handleCustomerFieldChange("paymentTerms", paymentTerms);
                    }}
                  />
                  {customerFieldErrors.paymentTerms && (
                    <span
                      className="quotation-customer-field-error"
                      role="alert"
                    >
                      {customerFieldErrors.paymentTerms}
                    </span>
                  )}
                </div>
              </div>

              <div
                className="quotation-modal-switch-row"
                onClick={() =>
                  !isCustomerSaving &&
                  handleCustomerFieldChange(
                    "autoReminders",
                    !newCustomerForm.autoReminders
                  )
                }
              >
                <div
                  className={`quotation-modal-switch-track${
                    newCustomerForm.autoReminders ? " active" : ""
                  }`}
                >
                  <div className="quotation-modal-switch-thumb" />
                </div>
                <span className="quotation-modal-switch-label">
                  Send invoice reminders automatically
                </span>
              </div>
              {customerFieldErrors.autoReminders && (
                <span className="quotation-customer-field-error" role="alert">
                  {customerFieldErrors.autoReminders}
                </span>
              )}

              <p className="quotation-modal-caption">
                Address, bank details and logo can be added later from Contacts.
              </p>
            </div>

            <div className="quotation-modal-footer">
              <button
                type="button"
                className="quotation-modal-cancel-btn"
                disabled={isCustomerSaving}
                onClick={customerModalChanges.requestClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quotation-modal-submit-btn"
                disabled={isCustomerSaving}
                onClick={handleQuickCreateCustomer}
              >
                {isCustomerSaving
                  ? "Saving..."
                  : editingCustomer
                  ? "Save changes"
                  : "Save customer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {customerToDelete && (
        <div
          className="quotation-dialog-scrim"
          onClick={() => {
            if (!isCustomerDeleting) setCustomerToDelete(null);
          }}
        >
          <div
            className="quotation-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quotation-delete-customer-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="quotation-modal-header">
              <h3 id="quotation-delete-customer-title">Delete customer?</h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                disabled={isCustomerDeleting}
                aria-label="Close"
                onClick={() => setCustomerToDelete(null)}
              >
                <LuX />
              </button>
            </div>
            <div className="quotation-modal-body">
              <p>
                Delete {customerToDelete.companyName || customerToDelete.name}?
                This cannot be undone. Customers used in quotations, invoices or
                RCTI cannot be deleted.
              </p>
              {customerDeleteError && (
                <div className="quotation-modal-alert" role="alert">
                  <LuCircleAlert />
                  <span>{customerDeleteError}</span>
                </div>
              )}
            </div>
            <div className="quotation-modal-footer">
              <button
                type="button"
                className="quotation-modal-cancel-btn"
                disabled={isCustomerDeleting}
                onClick={() => setCustomerToDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quotation-modal-submit-btn quotation-customer-delete-confirm"
                disabled={isCustomerDeleting}
                aria-busy={isCustomerDeleting}
                onClick={handleDeleteCustomer}
              >
                {isCustomerDeleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <LuTrash2 />
                )}{" "}
                Delete customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QUICK-CREATE CATALOGUE ITEM MODAL (Figma Node 722:57203) */}
      {isItemModalOpen && (
        <CatalogueItemDialog
          accountOptions={catalogueAccountOptions}
          darkMode={darkMode}
          error={itemModalError}
          isSaving={isItemSaving}
          onChange={(name, value) => {
            setNewItemForm((previous) => ({
              ...previous,
              [name]: value,
              ...(name === "discountType" && value === "None"
                ? { discountValue: "" }
                : {}),
            }));
            if (itemModalError === name) setItemModalError("");
          }}
          onClose={() => setIsItemModalOpen(false)}
          onSubmit={handleQuickCreateItem}
          submitLabel="Save & add to quote"
          values={newItemForm}
        />
      )}
      {editingCatalogueItem && (
        <AddItemDialog
          key={editingCatalogueItem.item._id}
          accountOptions={catalogueAccountOptions}
          businessUserID={loginDetails?.businessUserID}
          darkMode={darkMode}
          item={editingCatalogueItem.item}
          onClose={() => setEditingCatalogueItem(null)}
          onSaved={handleCatalogueItemUpdated}
        />
      )}
      {/* SEND QUOTE MODAL (Figma Node 574:39806) */}
      {isSendModalOpen && (
        <div
          className="quotation-dialog-scrim"
          data-clickable="true"
          onClick={() => {
            if (!sendingEmail && !isSavingQuote) setIsSendModalOpen(false);
          }}
        >
          <div
            className="quotation-send-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quotation-editor-send-title"
            data-clickable={false}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quotation-dialog-header">
              <h3 id="quotation-editor-send-title">Send quote</h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                disabled={sendingEmail || isSavingQuote}
                onClick={() => setIsSendModalOpen(false)}
              >
                <LuX />
              </button>
            </div>

            <div className="quotation-dialog-body">
              <QuotationRecipientField
                id="editor-send-recipient"
                primaryEmail={sendRecipient}
                disabled={sendingEmail || isSavingQuote}
                primaryError={getRecipientEmailError(sendRecipient, true)}
                additionalError={additionalRecipientsError}
                onChange={(emails) => {
                  setAdditionalRecipients(emails);
                  setSendError("");
                }}
                onError={setAdditionalRecipientsError}
              />
              {getRecipientEmailError(sendRecipient, true) && (
                <button
                  type="button"
                  className="quotation-dialog-cancel-btn"
                  onClick={() => {
                    setIsSendModalOpen(false);
                    window.requestAnimationFrame(() => {
                      const input = document.getElementById(
                        "quotation-recipient-email"
                      );
                      input?.focus({ preventScroll: true });
                      input?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    });
                  }}
                >
                  Update recipient email in quote
                </button>
              )}

              <div className="quotation-dialog-field">
                <label>Subject</label>
                <input
                  value={sendSubject}
                  onChange={(e) => setSendSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>

              <div className="quotation-dialog-preview-section">
                <div className="quotation-dialog-preview-heading">
                  <LuEye />
                  <span>Email preview</span>
                </div>
                <div className="quotation-dialog-email-card">
                  <p className="quotation-email-greeting">
                    Hello {values.name || "Customer"},
                  </p>
                  <p className="quotation-email-text">
                    Please find quote {values.quotationNumber || "QUO"} for $
                    {formatCurrencyAUD(parseFloat(values.finalAmount) || 0)}{" "}
                    {displayCurrencyCode} from {organisation}. You can review
                    the details and approve your quote online.
                  </p>
                  <p className="quotation-email-validity">
                    {getQuotationValidityText(values.validUpto)}
                  </p>
                  <span className="quotation-email-preview-cta">
                    Review &amp; approve
                  </span>
                  {values.quotationStatus === "Approved" && (
                    <div className="quotation-approval-success" role="status">
                      <LuCheck aria-hidden="true" />
                      Approved
                    </div>
                  )}
                  <div className="quotation-email-signoff">
                    <p>Thanks,</p>
                    <p>{organisation}</p>
                  </div>
                </div>
              </div>

              <div className="quotation-dialog-delivery-options">
                <label className="quotation-dialog-checkbox">
                  <input
                    type="checkbox"
                    checked={attachPdf}
                    onChange={(e) => setAttachPdf(e.target.checked)}
                  />
                  <span>Attach quote PDF</span>
                </label>
                <label className="quotation-dialog-checkbox">
                  <input
                    type="checkbox"
                    checked={sendCopy}
                    onChange={(e) => setSendCopy(e.target.checked)}
                  />
                  <span>Send me a copy</span>
                </label>
              </div>
              {sendError && (
                <p className="quotation-field-error" role="alert">
                  {sendError}
                </p>
              )}
            </div>

            <div className="quotation-dialog-footer">
              <button
                type="button"
                className="quotation-dialog-cancel-btn"
                disabled={sendingEmail || isSavingQuote}
                onClick={() => setIsSendModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quotation-dialog-send-btn"
                disabled={
                  sendingEmail ||
                  isSavingQuote ||
                  Boolean(getRecipientEmailError(sendRecipient, true))
                }
                onClick={handleSendQuoteSubmit}
              >
                <LuSend />
                <span>{sendingEmail ? "Sending..." : "Send quote"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {sendError && !isSendModalOpen && (
        <div
          className="quotation-dialog-scrim"
          data-clickable="true"
          onClick={() => setSendError("")}
        >
          <div
            className="quotation-modal-dialog quotation-send-failed-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="quotation-send-failed-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="quotation-modal-header">
              <h3 id="quotation-send-failed-title">Quote wasn’t sent</h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                aria-label="Close"
                onClick={() => setSendError("")}
              >
                <LuX />
              </button>
            </div>
            <div className="quotation-modal-body quotation-send-failed-body">
              <LuCircleAlert />
              <div>
                <strong>Delivery failed</strong>
                <p>{sendError}</p>
              </div>
            </div>
            <div className="quotation-modal-footer">
              <button
                type="button"
                className="quotation-modal-cancel-btn"
                onClick={() => setSendError("")}
              >
                Close
              </button>
              <button
                type="button"
                className="quotation-modal-submit-btn"
                onClick={() => {
                  setSendError("");
                  setIsSendModalOpen(true);
                }}
              >
                Review and retry
              </button>
            </div>
          </div>
        </div>
      )}

      <DocumentSentSuccessDialog
        open={Boolean(sentQuotationId)}
        documentType="quote"
        documentNumber={values.quotationNumber}
        recipient={sendRecipient}
        onClose={() => setSentQuotationId("")}
        onViewDetails={() => navigate(`/quotations/view/${sentQuotationId}`)}
      />

      {unsavedChanges.dialog}
      {customerModalChanges.dialog}

      {/* ATTACHMENT DOCUMENT VIEWER MODAL */}
      {viewDocumentInModal && (
        <div
          className="quotation-dialog-scrim document-preview-overlay"
          data-clickable="true"
          onClick={() => setViewDocumentInModal(false)}
        >
          <div
            className="quotation-document-preview"
            role="dialog"
            aria-modal="true"
            aria-label="Attachment preview"
            data-clickable={false}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="quotation-document-preview-close"
              aria-label="Close document preview"
              onClick={() => setViewDocumentInModal(false)}
            >
              <LuX />
            </button>
            {selectedFileType === "image" ? (
              <img src={documentFileURL} alt="Attachment Preview" />
            ) : (
              <iframe
                title="Document Preview"
                className={
                  selectedFileType === "pdf"
                    ? "quotation-document-preview-pdf"
                    : undefined
                }
                src={
                  selectedFileType === "pdf"
                    ? `${documentFileURL}#toolbar=0&navpanes=0&view=FitH`
                    : documentFileURL
                }
              />
            )}
          </div>
        </div>
      )}

      {/* BILLING MODAL */}
      {showBillingModal && (
        <BillingModal
          open={showBillingModal}
          handleConfirm={() => navigate("/subscriptions")}
          handleCancel={() => setShowBillingModal(false)}
          closeModalClick={() => setShowBillingModal(false)}
          message={genericMessage.billingMsg}
          title={genericMessage.noCreditAvailableTitle}
        />
      )}
    </div>
  );
}

export default AddQuotation;
